import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type ReactNode,
} from 'react';
import {
  AlertTriangle,
  Building2,
  ClipboardList,
  FileText,
  Info,
  Lock,
  MapPin,
  Plus,
  Printer,
  Scale,
  ShieldCheck,
  Users,
  Wallet,
} from 'lucide-react';
import { Alert, Modal, message } from 'antd';
import { useSearchParams } from 'react-router-dom';
import {
  BudgetCompliancePanel,
  BudgetLawSplit,
  BudgetSummaryModal,
  Button,
  PeerMetricPanel,
  SearchableSelect,
} from '@/shared/components';
import type { SearchableOption } from '@/shared/components';
import {
  SchoolFollowUpsSection,
  type SchoolFollowUpsSectionHandle,
} from '@/features/reports/components/SchoolFollowUpsSection';
import { ADD_COMPLIANCE_ISSUE_LABEL } from '@/features/reports/components/FollowUpsEditor';
import { buildPeerGroupMetrics, type PeerGroupMetrics } from '@/features/reports/utils/peerGroupMetrics';
import { CalendarIcon, MapPinIcon } from '@/shared/ui/Icons';
import { useAuth } from '@/features/auth/hooks/useAuth';
import {
  getSchoolProfilePublicationRuleLabel,
  getSchoolProfileViewMode,
  normRoleKey,
  userHasRoleCaseInsensitive,
  type SchoolProfileViewMode,
} from '@/features/auth/roleAccess';
import type {
  BudgetSubmittedStatusKind,
  ReviewStatusKind,
  SchoolClassificationDashboardView,
  SchoolClassificationSchoolComparisonView,
  SchoolComparisonPeerGroupView,
} from '@/features/reports/api';
import {
  budgetSubmittedStatusBadgeClass,
  labelBudgetSubmittedStatus,
  labelReviewStatus,
  parseBudgetSubmittedStatus,
  parseReviewStatus,
  reviewStatusBadgeClass,
} from '@/features/reports/api';
import { isDashboardSchoolId } from '@/features/reports/hooks/useSchoolClassificationDashboardQuery';
import {
  schoolClassificationComparisonErrorMessage,
  useSchoolClassificationComparisonQuery,
} from '@/features/reports/hooks/useSchoolClassificationComparisonQuery';
import {
  classTuitionByProgramErrorMessage,
  useClassTuitionByProgramQuery,
} from '@/features/reports/hooks/useClassTuitionByProgramQuery';
import type { ClassTuitionByProgramRow } from '@/features/reports/api';
import { ClassificationWorkflowRequiredAlert } from '@/features/reports/components/ClassificationWorkflowRequiredAlert';
import { useSchoolBandsConfiguredForYear } from '@/features/reports/hooks/useSchoolBandsConfiguredForYear';
import { regionApi } from '@/features/region/api';
import { budgetsApi } from '@/features/budgets/api';
import { useDefaultYear } from '@/features/settings/hooks/useDashboardSettingsQuery';
import {
  DashboardSettingSourceNote,
  DashboardSettingSourceNotes,
} from '@/features/settings/components/DashboardSettingSourceNote';
import {
  useBudgetLawAbMinimumPercentResolved,
  useBudgetLawAbWarningBandPercentResolved,
  useSchoolProfileCompletenessGoodThresholdResolved,
  useSchoolProfileCompletenessWarningThresholdResolved,
  useSchoolProfileCostOutlierRatioResolved,
} from '@/features/settings/hooks/useResolvedDashboardSetting';
import {
  schoolWithFiltersApi,
  type SchoolWithFiltersItemDto,
} from '@/features/reports/api';

const mockData = {
  governance: {
    validationStatus: 'Submitted' as const,
    dataCompletenessPct: 85,
    reviewStatus: 'Under review' as const,
    publicationRule: 'Internal' as 'Internal' | 'Parent-facing',
  },
  identity: {
    enrollmentBand: 'Size band Tier 2 (500–800 students)',
    parentsCommitteeStatus: 'Active · last ordinary meeting 14 Sep 2025',
    /** Shown as badges when school id is not a dashboard GUID (non-API selection). */
    sampleSchoolBandName: 'Tier 2 · 500–800 students (sample)',
    sampleTuitionBandName: 'Mid tuition cluster B (sample)',
    sampleParentBandName: 'Parent fee band L2 (sample)',
  },
  kpis: {
    studentTeacherRatio: '16.1 : 1',
    avgTeacherSalaryUsd: 2840,
  },
  peer: {
    tuition: { school: 4680, peerMedian: 4320 },
    costPerStudent: { school: 4420, peerMedian: 4180 },
    peerBandNote: 'Peers: same enrollment band + region (n = 24). Comparison is indicative, not adjudicative.',
  },
  /** Sample financial overview when dashboard API is not used. */
  financial: {
    totalRevenueUsd: 2_450_000,
    totalExpensesUsd: 2_180_000,
    tuitionPerStudentUsd: 4680,
    costPerStudentUsd: 4420,
  },
} as const;

type ActiveSchoolCard = {
  id: string;
  name: string;
  code: string;
  region: string;
  district: string;
  totalStudents: number;
  tuitionUsd: number;
};

function readSchoolProfileDeepLink(search: string): {
  schoolId: string;
  year: string;
  governorateId: string;
} {
  const q = new URLSearchParams(search);
  return {
    schoolId: q.get('schoolId')?.trim() ?? '',
    year: q.get('year')?.trim() ?? '',
    governorateId: q.get('governorateId')?.trim() ?? '',
  };
}

function formatSchoolApiLabel(item: SchoolWithFiltersItemDto): string {
  const name = item.schoolName?.trim() || 'School';
  const code = item.code?.trim() || item.educationalCenterNumber?.trim();
  return code ? `${name} (${code})` : name;
}

/** Scope column: audience (role / view mode) + governorate when selected. */
function formatAudienceScope(roles: string[] | undefined, governorateLabel: string): string {
  if (!roles?.length) return '—';
  const mode = getSchoolProfileViewMode(roles);
  const gov = governorateLabel.trim() ? governorateLabel.trim() : '';
  if (mode === 'mehe') {
    const keys = roles.map(normRoleKey);
    const tier = keys.some((k) => k === 'superadmin')
      ? 'Super Admin'
      : keys.some((k) => k === 'admin')
        ? 'Admin'
        : 'Ministry';
    const base = `${tier} (MEHE)`;
    return gov ? `${base} · ${gov}` : base;
  }
  if (mode === 'school') return gov ? `School · ${gov}` : 'School';
  return gov ? `Parent · ${gov}` : 'Parent';
}

function publicationRulePillClass(mode: SchoolProfileViewMode): string {
  switch (mode) {
    case 'mehe':
      return 'bg-slate-800 text-white ring-1 ring-slate-900/30 shadow-sm';
    case 'school':
      return 'bg-sky-700 text-white ring-1 ring-sky-900/20 shadow-sm';
    default:
      return 'bg-violet-700 text-white ring-1 ring-violet-900/20 shadow-sm';
  }
}

interface GoverningStripProps {
  reportingYear: string;
  scope: string;
  validationStatus: string;
  validationStatusKind: BudgetSubmittedStatusKind | null;
  dataCompletenessPct: number;
  reviewStatus: string;
  reviewStatusKind: ReviewStatusKind | null;
  publicationRule: string;
  publicationAudience: SchoolProfileViewMode;
  loading: boolean;
  error: string | null;
}

function GoverningCompletenessVisual({
  pct,
  loading,
  goodThreshold,
  warningThreshold,
}: {
  pct: number;
  loading: boolean;
  goodThreshold: number;
  warningThreshold: number;
}) {
  const r = 14;
  const c = 2 * Math.PI * r;
  const dash = (pct / 100) * c;
  const completenessTrack =
    pct >= goodThreshold
      ? 'from-emerald-500 to-teal-600'
      : pct >= warningThreshold
        ? 'from-amber-500 to-orange-600'
        : 'from-rose-500 to-red-600';
  const ringStroke =
    pct >= goodThreshold
      ? 'stroke-emerald-600'
      : pct >= warningThreshold
        ? 'stroke-amber-600'
        : 'stroke-rose-600';

  if (loading) {
    return <span className="text-slate-400">…</span>;
  }

  return (
    <div className="mx-auto flex max-w-[200px] flex-col items-center gap-2 sm:flex-row sm:justify-center print:max-w-none print:gap-1">
      <div className="relative size-10 shrink-0 print:size-8">
        <svg
          width="40"
          height="40"
          viewBox="0 0 40 40"
          className="-rotate-90 print:h-8 print:w-8"
          aria-hidden
        >
          <circle cx="20" cy="20" r={r} className="fill-none stroke-slate-200" strokeWidth="4" />
          <circle
            cx="20"
            cy="20"
            r={r}
            className={`fill-none ${ringStroke}`}
            strokeWidth="4"
            strokeLinecap="round"
            strokeDasharray={c}
            strokeDashoffset={c - dash}
          />
        </svg>
        <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-[9px] font-black tabular-nums text-slate-800 print:text-[6px]">
          {pct}%
        </span>
      </div>
      <div className="w-full min-w-0 flex-1">
        <div className="mb-0.5 flex justify-between text-[9px] font-bold uppercase tracking-wide text-slate-500 print:text-[6px]">
          <span>0%</span>
          <span>100%</span>
        </div>
        <div className="h-2 overflow-hidden rounded-full bg-slate-200 shadow-inner print:h-1.5">
          <div
            className={`h-full rounded-full bg-gradient-to-r ${completenessTrack}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
    </div>
  );
}

function GoverningStrip({
  reportingYear,
  scope,
  validationStatus,
  validationStatusKind,
  dataCompletenessPct,
  reviewStatus,
  reviewStatusKind,
  publicationRule,
  publicationAudience,
  loading,
  error,
}: GoverningStripProps) {
  const pct = Math.min(100, Math.max(0, Math.round(dataCompletenessPct)));
  const completenessGoodResolved = useSchoolProfileCompletenessGoodThresholdResolved();
  const completenessWarningResolved = useSchoolProfileCompletenessWarningThresholdResolved();

  return (
    <section className="print:break-inside-avoid" aria-label="Governing strip">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex min-w-0 items-start gap-2">
          <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg bg-slate-800 text-white shadow-sm ring-1 ring-slate-900/20 print:size-6">
            <Lock className="size-4 print:size-3" aria-hidden />
          </span>
          <div>
            <h2 className="text-sm font-bold tracking-tight text-slate-900 print:text-[10px]">
              1. Governing strip <span className="font-semibold text-slate-500">(locked context)</span>
            </h2>
            <p className="mt-0.5 text-[11px] leading-snug text-slate-600 print:text-[7px]">
              Official governed header for audit and publication. Data completeness combines ring (target) and linear
              track (actual).
            </p>
          </div>
        </div>
        <span className="inline-flex w-fit items-center rounded-md border border-slate-400/60 bg-slate-200/90 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-700 print:hidden">
          Governed
        </span>
      </div>
      <div className="mt-3 overflow-hidden rounded-xl border-2 border-slate-400/50 bg-slate-100/90 shadow-inner ring-1 ring-slate-300/60 print:mt-1.5 print:border-slate-500 print:shadow-none">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] border-collapse text-sm print:min-w-0 print:text-[7px]">
            <thead>
              <tr className="bg-slate-800 text-white print:bg-slate-900">
                {[
                  'Reporting Year',
                  'Scope',
                  'Validation Status',
                  'Data Completeness',
                  'Review Status',
                  'Publication Rule',
                ].map((h) => (
                  <th
                    key={h}
                    className="border-b border-r border-white/10 px-3 py-2.5 text-center text-[11px] font-bold uppercase tracking-wide last:border-r-0 print:px-1 print:py-1 print:text-[6px]"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr className="bg-slate-50 print:bg-white">
                <td className="border-l border-t border-slate-300/90 px-3 py-3 text-center align-middle first:border-l-0 print:px-1 print:py-1">
                  <span className="inline-flex min-h-[1.75rem] items-center justify-center rounded-md border border-slate-200 bg-white px-2.5 text-sm font-bold tabular-nums text-slate-900 print:min-h-0 print:text-[8px]">
                    {reportingYear || '—'}
                  </span>
                </td>
                <td className="border-l border-t border-slate-300/90 px-3 py-3 text-center text-sm font-semibold leading-snug text-slate-800 print:px-1 print:py-1 print:text-[7px]">
                  {scope}
                </td>
                <td className="border-l border-t border-slate-300/90 px-3 py-3 text-center align-middle print:px-1 print:py-1">
                  {loading ? (
                    <span className="text-slate-400">…</span>
                  ) : (
                    <span
                      className={`inline-flex max-w-[11rem] items-center justify-center rounded-md px-2.5 py-1 text-xs font-bold leading-tight print:max-w-none print:px-1 print:py-0.5 print:text-[6px] ${budgetSubmittedStatusBadgeClass(validationStatusKind, validationStatus)}`}
                    >
                      {validationStatus}
                    </span>
                  )}
                </td>
                <td className="border-l border-t border-slate-300/90 px-3 py-3 text-center align-middle print:px-1 print:py-1">
                  <GoverningCompletenessVisual
                    pct={pct}
                    loading={loading}
                    goodThreshold={completenessGoodResolved.value}
                    warningThreshold={completenessWarningResolved.value}
                  />
                </td>
                <td className="border-l border-t border-slate-300/90 px-3 py-3 text-center align-middle print:px-1 print:py-1">
                  {loading ? (
                    <span className="text-slate-400">…</span>
                  ) : (
                    <span
                      className={`inline-flex max-w-[11rem] items-center justify-center rounded-md px-2.5 py-1 text-xs font-bold leading-tight print:max-w-none print:px-1 print:py-0.5 print:text-[6px] ${reviewStatusBadgeClass(reviewStatusKind, reviewStatus)}`}
                    >
                      {reviewStatus}
                    </span>
                  )}
                </td>
                <td className="border-l border-t border-slate-300/90 px-3 py-3 text-center align-middle print:px-1 print:py-1">
                  <span
                    className={`inline-flex max-w-[12rem] items-center justify-center rounded-md px-2.5 py-1.5 text-xs font-bold leading-tight print:max-w-none print:px-1 print:py-0.5 print:text-[6px] ${publicationRulePillClass(publicationAudience)}`}
                  >
                    {publicationRule}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      {error ? (
        <p className="mt-3 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-900 print:mt-1 print:p-1 print:text-[7px]">
          <span className="mt-0.5 inline-block size-1.5 shrink-0 rounded-full bg-red-500" aria-hidden />
          {error}
        </p>
      ) : null}
      <DashboardSettingSourceNotes
        className="mt-3 print:hidden"
        resolved={[completenessGoodResolved, completenessWarningResolved]}
      />
    </section>
  );
}

function sectionAuditBadgeFromReview(
  reviewKind: ReviewStatusKind | null,
  reviewLabelFallback: string
): { text: string; className: string } {
  if (reviewKind === null) {
    return {
      text: 'Sample',
      className: 'bg-slate-200 text-slate-800 ring-1 ring-slate-300',
    };
  }
  switch (reviewKind) {
    case 0:
      return {
        text: 'Validated',
        className: 'bg-emerald-700 text-white ring-1 ring-emerald-900/20',
      };
    case 2:
      return {
        text: 'Under review',
        className: 'bg-sky-700 text-white ring-1 ring-sky-900/20',
      };
    case 1:
      return {
        text: 'Clarify',
        className: 'bg-amber-600 text-white ring-1 ring-amber-800/30',
      };
    case 3:
      return {
        text: 'Corrected',
        className: 'bg-teal-800 text-white ring-1 ring-teal-950/30',
      };
    default:
      return {
        text: reviewLabelFallback.slice(0, 24) || '—',
        className: 'bg-slate-600 text-white ring-1 ring-slate-800/20',
      };
  }
}

function ReportSection({
  number,
  title,
  icon: Icon,
  children,
  className = '',
  auditBadge,
  headerActions,
}: {
  number: number;
  title: string;
  icon: ComponentType<{ className?: string }>;
  children: ReactNode;
  className?: string;
  auditBadge?: { text: string; className: string };
  headerActions?: ReactNode;
}) {
  return (
    <section
      className={`overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-sm ring-1 ring-slate-100 print:break-inside-avoid print:rounded-md print:border-slate-400 print:shadow-none ${className}`}
    >
      <div className="flex flex-wrap items-center gap-3 border-b border-slate-200/90 bg-gradient-to-r from-slate-100/90 to-white px-4 py-3 print:gap-2 print:px-2 print:py-1.5 print:bg-slate-100">
        <span
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-800 text-sm font-bold text-white shadow-sm print:h-7 print:w-7 print:text-xs"
          aria-hidden
        >
          {number}
        </span>
        <Icon className="size-5 shrink-0 text-slate-600 print:size-4" aria-hidden />
        <h2 className="min-w-0 flex-1 text-xs font-bold uppercase tracking-[0.12em] text-slate-800 print:text-[8px]">
          {title}
        </h2>
        {headerActions ? (
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 print:hidden">{headerActions}</div>
        ) : null}
        {auditBadge ? (
          <span
            className={`shrink-0 rounded-sm px-2 py-1 text-[10px] font-bold uppercase tracking-wide print:px-1 print:py-0.5 print:text-[6px] ${auditBadge.className}`}
          >
            {auditBadge.text}
          </span>
        ) : null}
      </div>
      <div className="p-4 sm:p-5 print:p-2 print:text-[8px]">{children}</div>
    </section>
  );
}

function SchoolProfileHero({
  school,
  reportingYearLabel,
}: {
  school: ActiveSchoolCard;
  reportingYearLabel: string;
}) {
  return (
    <div className="relative overflow-hidden rounded-2xl border border-slate-800/20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-5 text-white shadow-lg print:rounded-md print:border-slate-900 print:bg-slate-900 print:p-2 print:shadow-none">
      <div
        className="pointer-events-none absolute -right-16 -top-16 size-48 rounded-full bg-sky-500/15 blur-2xl print:hidden"
        aria-hidden
      />
      <div className="relative flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">Selected school</p>
          <h3 className="mt-1 text-xl font-bold leading-tight tracking-tight sm:text-2xl print:text-sm print:leading-tight">
            {school.name}
          </h3>
          <div className="mt-3 flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold ring-1 ring-white/15 backdrop-blur-sm">
              <Building2 className="size-3.5 shrink-0 opacity-90" aria-hidden />
              {school.code}
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold ring-1 ring-white/15 backdrop-blur-sm">
              <MapPin className="size-3.5 shrink-0 opacity-90" aria-hidden />
              {school.region}
            </span>
            <span className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-slate-200 ring-1 ring-white/10 backdrop-blur-sm">
              {school.district}
            </span>
          </div>
        </div>
        <div className="shrink-0 rounded-xl bg-white/10 px-4 py-3 text-center ring-1 ring-white/15 backdrop-blur-sm print:bg-slate-800">
          <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Reporting year</p>
          <p className="mt-1 text-2xl font-bold tabular-nums tracking-tight">{reportingYearLabel || '—'}</p>
        </div>
      </div>
    </div>
  );
}

/** School cost materially above peer average (governed outlier hint). */
function isCostOutlierVsPeer(schoolCost: number, peerAvg: number, ratioThreshold: number): boolean {
  if (!Number.isFinite(schoolCost) || !Number.isFinite(peerAvg) || peerAvg <= 0) return false;
  if (!Number.isFinite(ratioThreshold) || ratioThreshold <= 0) return false;
  return schoolCost / peerAvg >= ratioThreshold;
}

function PeerGroupComparisonBlock({
  group,
  schoolTuition,
  schoolCost,
  comparison,
  onOpenBudget,
}: {
  group: SchoolComparisonPeerGroupView;
  schoolTuition: number;
  schoolCost: number;
  comparison?: SchoolClassificationSchoolComparisonView;
  onOpenBudget?: (budgetId: string, meta?: { schoolName?: string }) => void;
}) {
  const costOutlierRatioResolved = useSchoolProfileCostOutlierRatioResolved();
  const peer = useMemo(
    () => buildPeerGroupMetrics(group, schoolTuition, schoolCost),
    [group, schoolTuition, schoolCost]
  );
  const costPeerRef =
    peer.avgCostPerStudentUSD > 0 ? peer.avgCostPerStudentUSD : peer.medianCostPerStudentUSD;
  const costOutlier = isCostOutlierVsPeer(
    peer.costPerStudentUSD,
    costPeerRef,
    costOutlierRatioResolved.value
  );

  const tuitionBandOverlay = useMemo(() => {
    const b = comparison?.targetTuitionBand;
    if (!b) return undefined;
    return {
      min: b.minValue,
      max: b.maxValue,
      label: `Tuition band: ${b.name}`,
    };
  }, [comparison?.targetTuitionBand]);

  const parentTuitionBandOverlay = useMemo(() => {
    const b = comparison?.targetParentTuitionBand;
    if (!b) return undefined;
    return {
      min: b.minValue,
      max: b.maxValue,
      label: `Parent-facing tuition band: ${b.name}`,
    };
  }, [comparison?.targetParentTuitionBand]);

  const costHeaderRight = useMemo(() => {
    if (!costOutlier) return undefined;
    return (
      <span
        className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-900 ring-1 ring-amber-200 print:text-[6px] print:px-1"
        title="This school’s cost per student is materially above the peer reference (average, else median)."
      >
        <AlertTriangle className="size-3.5 shrink-0 print:size-2.5" aria-hidden />
        Outlier
      </span>
    );
  }, [costOutlier]);

  return (
    <PeerMetricPanel
      title={group.label}
      peer={peer}
      tuitionBandOverlay={tuitionBandOverlay}
      parentTuitionBandOverlay={parentTuitionBandOverlay}
      costHeaderRight={costHeaderRight}
      onOpenBudget={onOpenBudget}
    />
  );
}

function demoBulletDisplayAndRaw(actual: number, avg: number, median: number) {
  const vals = [actual, avg, median].filter((x) => Number.isFinite(x));
  const lo = Math.min(...vals);
  const hi = Math.max(...vals);
  const inner = hi > lo ? hi - lo : Math.max(Math.abs(lo) * 0.06, 420);
  return {
    displayMin: lo - inner * 0.15,
    displayMax: hi + inner * 0.15,
    rawMin: lo - inner * 0.45,
    rawMax: hi + inner * 0.45,
  };
}

function PeerDemoBulletCharts({
  chartSchoolTuition,
  chartSchoolCost,
}: {
  chartSchoolTuition: number;
  chartSchoolCost: number;
}) {
  const nDemo = 24;
  const tPeer = mockData.peer.tuition.peerMedian;
  const tAvg = tPeer * 1.06;
  const tActual = chartSchoolTuition;
  const tD = demoBulletDisplayAndRaw(tActual, tAvg, tPeer);
  const cPeer = mockData.peer.costPerStudent.peerMedian;
  const cAvg = cPeer * 1.04;
  const cActual = chartSchoolCost;
  const cD = demoBulletDisplayAndRaw(cActual, cAvg, cPeer);
  const peer: PeerGroupMetrics = {
    label: 'Sample peer band',
    schoolCount: nDemo,
    costPerStudentUSD: cActual,
    avgCostPerStudentUSD: cAvg,
    medianCostPerStudentUSD: cPeer,
    displayMinCostPerStudentUSD: cD.displayMin,
    displayMaxCostPerStudentUSD: cD.displayMax,
    minCostPerStudentUSD: cD.rawMin,
    maxCostPerStudentUSD: cD.rawMax,
    tuitionPerStudentUSD: tActual,
    avgTuitionPerStudentUSD: tAvg,
    medianTuitionPerStudentUSD: tPeer,
    displayMinTuitionPerStudentUSD: tD.displayMin,
    displayMaxTuitionPerStudentUSD: tD.displayMax,
    minTuitionPerStudentUSD: tD.rawMin,
    maxTuitionPerStudentUSD: tD.rawMax,
    bandTuitionMin: tD.displayMin + (tD.displayMax - tD.displayMin) * 0.15,
    bandTuitionMax: tD.displayMin + (tD.displayMax - tD.displayMin) * 0.55,
  };
  return (
    <div className="mt-5 print:mt-2">
      <PeerMetricPanel title="Sample peer band" peer={peer} />
    </div>
  );
}

function PeerComparisonSection({
  schoolId,
  schoolName,
  year,
  reportingYearLabel,
  comparison,
  comparisonLoading,
  comparisonError,
  chartSchoolTuition,
  chartSchoolCost,
}: {
  schoolId: string;
  schoolName: string;
  year: string;
  reportingYearLabel: string;
  comparison: SchoolClassificationSchoolComparisonView | undefined;
  comparisonLoading: boolean;
  comparisonError: string | null;
  chartSchoolTuition: number;
  chartSchoolCost: number;
}) {
  const isGuid = isDashboardSchoolId(schoolId);
  const costOutlierRatioResolved = useSchoolProfileCostOutlierRatioResolved();
  const [budgetModal, setBudgetModal] = useState<{
    open: boolean;
    budgetId?: string;
    /** Min/max budget drill: school name from API for modal title until payload loads. */
    drillSchoolName?: string;
  }>({ open: false, budgetId: undefined, drillSchoolName: undefined });
  const handleOpenBudgetModal = useCallback((id: string, meta?: { schoolName?: string }) => {
    setBudgetModal({
      open: true,
      budgetId: id,
      drillSchoolName: meta?.schoolName?.trim() || undefined,
    });
  }, []);
  const handleCloseBudgetModal = useCallback(() => {
    setBudgetModal({ open: false, budgetId: undefined, drillSchoolName: undefined });
  }, []);
  const budgetSummaryModal =
    budgetModal.open && budgetModal.budgetId ? (
      <BudgetSummaryModal
        open
        budgetId={budgetModal.budgetId}
        onClose={handleCloseBudgetModal}
        contextSchoolName={budgetModal.drillSchoolName ?? schoolName}
        contextYear={year}
      />
    ) : null;

  if (!isGuid) {
    return (
      <>
        <p className="text-sm leading-relaxed text-slate-600 print:text-[8px]">{mockData.peer.peerBandNote}</p>
        <PeerDemoBulletCharts chartSchoolTuition={chartSchoolTuition} chartSchoolCost={chartSchoolCost} />
        {budgetSummaryModal}
      </>
    );
  }

  const intro = (
    <>
      <p className="text-sm leading-relaxed text-slate-600 print:text-[8px]">
        Each peer band shows <strong>cost</strong> then <strong>tuition</strong> on a scale from the <strong>lowest</strong>{' '}
        to <strong>highest</strong> peer in that group (with slight padding). A <strong>dashed band</strong> marks typical
        peers (~p10–p90). Tuition may add governed <strong>bands</strong> from the service. Hover markers for values and
        deltas. Reporting year <strong>{reportingYearLabel || '—'}</strong>. Figures are indicative.
      </p>
      <DashboardSettingSourceNote resolved={costOutlierRatioResolved} className="mt-2 print:hidden" />
    </>
  );

  if (comparisonLoading) {
    return (
      <>
        {intro}
        <p className="mt-3 text-sm font-medium text-slate-500">Loading peer comparison…</p>
        {budgetSummaryModal}
      </>
    );
  }

  if (comparisonError) {
    return (
      <>
        {intro}
        <p className="mt-2 text-sm text-amber-800">{comparisonError}</p>
        <p className="mt-1 text-xs text-slate-500">Showing sample peer medians until the comparison service succeeds.</p>
        <PeerDemoBulletCharts chartSchoolTuition={chartSchoolTuition} chartSchoolCost={chartSchoolCost} />
        {budgetSummaryModal}
      </>
    );
  }

  const g1 = comparison?.peerGroupCategory1;
  const g2 = comparison?.peerGroupCategory2;
  const g3 = comparison?.peerGroupCategory3;

  if (!g1 && !g2 && !g3) {
    return (
      <>
        {intro}
        <p className="mt-3 text-sm text-slate-600">No peer group breakdown was returned for this school.</p>
        {budgetSummaryModal}
      </>
    );
  }

  return (
    <>
      {intro}
      <div className="mt-5 space-y-5">
        {g1 ? (
          <PeerGroupComparisonBlock
            group={g1}
            schoolTuition={chartSchoolTuition}
            schoolCost={chartSchoolCost}
            comparison={comparison}
            onOpenBudget={handleOpenBudgetModal}
          />
        ) : null}
        {g2 ? (
          <PeerGroupComparisonBlock
            group={g2}
            schoolTuition={chartSchoolTuition}
            schoolCost={chartSchoolCost}
            comparison={comparison}
            onOpenBudget={handleOpenBudgetModal}
          />
        ) : null}
        {g3 ? (
          <PeerGroupComparisonBlock
            group={g3}
            schoolTuition={chartSchoolTuition}
            schoolCost={chartSchoolCost}
            comparison={comparison}
            onOpenBudget={handleOpenBudgetModal}
          />
        ) : null}
      </div>
      {budgetSummaryModal}
    </>
  );
}

interface SchoolProfileLoadedViewProps {
  school: ActiveSchoolCard;
  year: string;
  reportingYearLabel: string;
  scopeLabel: string;
  publicationRuleLabel: string;
  profileViewMode: SchoolProfileViewMode;
  dashboard: SchoolClassificationDashboardView | undefined;
  dashboardLoading: boolean;
  dashboardError: string | null;
  comparison: SchoolClassificationSchoolComparisonView | undefined;
  comparisonLoading: boolean;
  comparisonError: string | null;
}

const KPI_ACCENT = [
  'border-l-amber-500',
  'border-l-sky-500',
  'border-l-emerald-500',
  'border-l-violet-500',
  'border-l-rose-500',
] as const;

function formatStudentTeacherRatio(ratio: number): string {
  if (!Number.isFinite(ratio)) return '—';
  const x = Math.round(ratio * 10) / 10;
  const s = Number.isInteger(x) ? String(x) : x.toFixed(1);
  return `${s} : 1`;
}

function formatUsdWhole(amount: number): string {
  if (!Number.isFinite(amount)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(Math.round(amount));
}

/** Band labels from the API often include redundant fractional zeros, e.g. "(317.00-636.00)". */
function formatBandLabelBoundaries(label: string): string {
  return label.replace(/\d+\.\d+/g, (m) => {
    const n = Number(m);
    if (!Number.isFinite(n)) return m;
    return n % 1 === 0 ? String(Math.trunc(n)) : m;
  });
}

const FIN_SUMMARY_ACCENT = [
  'border-l-teal-500',
  'border-l-emerald-500',
  'border-l-sky-500',
  'border-l-amber-500',
  'border-l-indigo-500',
] as const;

function IdentityFieldShell({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="group rounded-xl border border-slate-200/90 bg-gradient-to-b from-white to-slate-50/90 px-4 py-3 shadow-sm ring-1 ring-slate-100/80 transition hover:border-slate-300 hover:shadow-md print:border-slate-300">
      <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500">{label}</p>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function enrollmentSchoolPillClass(filled: boolean): string {
  return filled
    ? 'bg-slate-800 text-white shadow-sm ring-1 ring-slate-900/25'
    : 'bg-slate-100 text-slate-600 ring-1 ring-slate-200';
}

function enrollmentTuitionPillClass(filled: boolean): string {
  return filled
    ? 'bg-sky-700 text-white shadow-sm ring-1 ring-sky-900/20'
    : 'bg-sky-50 text-sky-800 ring-1 ring-sky-200';
}

/** Always shows school + tuition as pills; API names, mock samples, or "—" for empty ministry payloads. */
function EnrollmentBandBadges({
  schoolId,
  dashboard,
  dashboardLoading,
  fallbackText,
}: {
  schoolId: string;
  dashboard: SchoolClassificationDashboardView | undefined;
  dashboardLoading: boolean;
  fallbackText: string;
}) {
  const isGuid = isDashboardSchoolId(schoolId);
  const waitForApi = isGuid && dashboardLoading;

  const apiSchool = dashboard?.schoolBandName?.trim();
  const apiTuition = dashboard?.tuitionBandName?.trim();

  const demoSchool = mockData.identity.sampleSchoolBandName;
  const demoTuition = mockData.identity.sampleTuitionBandName;

  if (waitForApi) {
    return <span className="text-sm font-medium text-slate-400">Loading classification…</span>;
  }

  const schoolText = formatBandLabelBoundaries(apiSchool || (!isGuid ? demoSchool : '—'));
  const tuitionText = formatBandLabelBoundaries(apiTuition || (!isGuid ? demoTuition : '—'));
  const schoolFilled = Boolean(apiSchool || !isGuid);
  const tuitionFilled = Boolean(apiTuition || !isGuid);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap gap-2">
        <span
          className={`inline-flex max-w-full min-w-0 items-center gap-2 rounded-full py-1 pl-2.5 pr-3 text-xs font-bold ${enrollmentSchoolPillClass(schoolFilled)}`}
          title={schoolText}
        >
          <span
            className={`shrink-0 rounded-md px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide ${
              schoolFilled ? 'bg-white/15 text-slate-200' : 'bg-white/80 text-slate-500'
            }`}
          >
            School band
          </span>
          <span className="truncate font-semibold">{schoolText}</span>
        </span>
        <span
          className={`inline-flex max-w-full min-w-0 items-center gap-2 rounded-full py-1 pl-2.5 pr-3 text-xs font-bold ${enrollmentTuitionPillClass(tuitionFilled)}`}
          title={tuitionText}
        >
          <span
            className={`shrink-0 rounded-md px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide ${
              tuitionFilled ? 'bg-white/15 text-sky-100' : 'bg-white text-sky-700'
            }`}
          >
            Tuition band
          </span>
          <span className="truncate font-semibold">{tuitionText}</span>
        </span>
      </div>
      {!isGuid ? (
        <p className="text-[11px] leading-snug text-slate-500">{fallbackText}</p>
      ) : null}
    </div>
  );
}

/** Parent band pill always; API name, mock sample, or "—"; committee note when no API parent name. */
function ParentsBandStatus({
  schoolId,
  dashboard,
  dashboardLoading,
  fallbackCommitteeText,
}: {
  schoolId: string;
  dashboard: SchoolClassificationDashboardView | undefined;
  dashboardLoading: boolean;
  fallbackCommitteeText: string;
}) {
  const isGuid = isDashboardSchoolId(schoolId);
  const waitForApi = isGuid && dashboardLoading;
  const apiParent = dashboard?.parentBandName?.trim();
  const demoParent = mockData.identity.sampleParentBandName;

  if (waitForApi) {
    return <span className="text-sm font-medium text-slate-400">Loading parent-facing tuition band…</span>;
  }

  const parentText = formatBandLabelBoundaries(apiParent || (!isGuid ? demoParent : '—'));
  const parentFilled = Boolean(apiParent || !isGuid);

  return (
    <div className="flex flex-col gap-2">
      <span
        className={`inline-flex w-full max-w-full min-w-0 flex-col items-stretch gap-2 rounded-2xl py-2 pl-2.5 pr-3 text-xs font-bold shadow-sm ring-1 ${
          parentFilled
            ? 'bg-violet-700 text-white ring-violet-900/20'
            : 'bg-violet-50 text-violet-900 ring-violet-200'
        }`}
        title={parentText}
      >
        <span
          className={`w-full min-w-0 self-start break-words rounded-md px-1.5 py-1 text-[8px] font-bold uppercase leading-snug tracking-wide sm:w-auto sm:shrink-0 sm:py-0.5 sm:text-[9px] ${
            parentFilled ? 'bg-white/15 text-violet-100' : 'bg-white text-violet-700'
          }`}
        >
          <span className="block sm:inline">Parent-facing </span>
          <span className="block sm:inline">tuition band</span>
        </span>
        <span
          className={`min-w-0 overflow-x-auto whitespace-nowrap text-sm font-semibold leading-tight [scrollbar-width:thin] ${
            parentFilled ? 'text-white' : 'text-violet-950'
          }`}
        >
          {parentText}
        </span>
      </span>
      {!apiParent ? (
        <p className="text-xs leading-relaxed text-slate-600">{fallbackCommitteeText}</p>
      ) : null}
    </div>
  );
}

interface SchoolProfileParentLoadedViewProps {
  school: ActiveSchoolCard;
  year: string;
  reportingYearLabel: string;
  scopeLabel: string;
  publicationRuleLabel: string;
  profileViewMode: SchoolProfileViewMode;
  dashboard: SchoolClassificationDashboardView | undefined;
  dashboardLoading: boolean;
  dashboardError: string | null;
  comparison: SchoolClassificationSchoolComparisonView | undefined;
  comparisonLoading: boolean;
  comparisonError: string | null;
}

/** Parent / ParentRole: tuition (USD) + peer comparison only — no school-size KPIs, financial drill-down, or compliance grid. */
function SchoolProfileParentLoadedView({
  school,
  year,
  reportingYearLabel,
  scopeLabel,
  publicationRuleLabel,
  profileViewMode,
  dashboard,
  dashboardLoading,
  dashboardError,
  comparison,
  comparisonLoading,
  comparisonError,
}: SchoolProfileParentLoadedViewProps) {
  const strip = useMemo(() => {
    const d = dashboard;
    const useApi = Boolean(d) && !dashboardLoading;
    const fallbackV = mockData.governance.validationStatus;
    const fallbackC = mockData.governance.dataCompletenessPct;
    const fallbackR = mockData.governance.reviewStatus;

    const pickStr = (api: string | undefined, fb: string) => {
      if (!useApi) return fb;
      const t = api?.trim();
      return t ? t : '—';
    };

    const completeness =
      useApi && d?.dataCompleteness != null && Number.isFinite(Number(d.dataCompleteness))
        ? Math.min(100, Math.max(0, Math.round(Number(d.dataCompleteness))))
        : fallbackC;

    const budgetKindFallback = parseBudgetSubmittedStatus(fallbackV);
    const reviewKindFallback = parseReviewStatus(fallbackR);

    const validationKind = useApi
      ? (d?.budgetSubmittedStatusKind ?? parseBudgetSubmittedStatus(d?.budgetSubmittedStatus))
      : budgetKindFallback;
    const reviewKind = useApi
      ? (d?.reviewStatusKind ?? parseReviewStatus(d?.reviewStatus))
      : reviewKindFallback;

    const validation =
      useApi
        ? pickStr(d?.budgetSubmittedStatus, fallbackV)
        : budgetKindFallback !== null
          ? labelBudgetSubmittedStatus(budgetKindFallback)
          : fallbackV;
    const review =
      useApi
        ? pickStr(d?.reviewStatus, fallbackR)
        : reviewKindFallback !== null
          ? labelReviewStatus(reviewKindFallback)
          : fallbackR;

    return {
      validation,
      validationKind,
      completeness,
      review,
      reviewKind,
    };
  }, [dashboard, dashboardLoading]);

  const peerChartSchoolTuition = useMemo(() => {
    const c = comparison;
    if (c?.targetTuitionPerStudentUSD != null && Number.isFinite(c.targetTuitionPerStudentUSD)) {
      return c.targetTuitionPerStudentUSD;
    }
    const d = dashboard;
    if (d?.tuitionPerStudentUSD != null && Number.isFinite(d.tuitionPerStudentUSD)) {
      return d.tuitionPerStudentUSD;
    }
    return school.tuitionUsd;
  }, [comparison, dashboard, school.tuitionUsd]);

  const peerChartSchoolCost = useMemo(() => {
    const c = comparison;
    if (c?.targetCostPerStudentUSD != null && Number.isFinite(c.targetCostPerStudentUSD)) {
      return c.targetCostPerStudentUSD;
    }
    const d = dashboard;
    if (d?.costPerStudentUSD != null && Number.isFinite(d.costPerStudentUSD)) {
      return d.costPerStudentUSD;
    }
    return mockData.peer.costPerStudent.school;
  }, [comparison, dashboard]);

  const parentPeerGroup = useMemo(() => {
    const c = comparison;
    if (!c) return null;
    if (c.peerGroupCategory3) return c.peerGroupCategory3;
    const legacy = [c.peerGroupCategory2, c.peerGroupCategory1].filter(
      (g): g is SchoolComparisonPeerGroupView => g != null
    );
    return legacy.find((g) => /parent/i.test(g.label)) ?? null;
  }, [comparison]);

  const tuitionUsdForParent = useMemo(() => {
    const d = dashboard;
    if (d?.tuitionPerStudentUSD != null && Number.isFinite(Number(d.tuitionPerStudentUSD))) {
      return { amount: Number(d.tuitionPerStudentUSD), subtitle: 'Per student · from school overview' as const };
    }
    if (d?.tuitionUSD != null && Number.isFinite(Number(d.tuitionUSD))) {
      return { amount: Number(d.tuitionUSD), subtitle: 'From school overview' as const };
    }
    if (school.tuitionUsd != null && Number.isFinite(school.tuitionUsd)) {
      return { amount: school.tuitionUsd, subtitle: 'From selected school record' as const };
    }
    return null;
  }, [dashboard, school.tuitionUsd]);

  return (
    <div className="space-y-5 font-[Inter,ui-sans-serif,system-ui,sans-serif] print:space-y-1.5 print:text-[8px] print:leading-snug">
      <SchoolProfileHero school={school} reportingYearLabel={reportingYearLabel || year || '—'} />

      <div className="rounded-2xl border border-violet-200 bg-violet-50/90 px-4 py-3 shadow-sm ring-1 ring-violet-100 print:border-violet-300 print:bg-violet-50 print:px-2 print:py-1.5">
        <div className="flex gap-2">
          <Info className="mt-0.5 size-4 shrink-0 text-violet-700" aria-hidden />
          <p className="text-sm leading-relaxed text-violet-950 print:text-[8px]">
            <strong>Family view.</strong> Tuition is shown in <strong>USD</strong>, with a simple comparison to similar
            schools. Detailed ministry classification tables are not included here.
          </p>
        </div>
      </div>

      <div className="rounded-2xl border-2 border-slate-400/40 bg-slate-200/40 p-4 shadow-sm ring-1 ring-slate-300/50 print:border-slate-500 print:bg-slate-100 print:p-2 print:shadow-none">
        <GoverningStrip
          reportingYear={reportingYearLabel || year || '—'}
          scope={scopeLabel}
          validationStatus={strip.validation}
          validationStatusKind={strip.validationKind}
          dataCompletenessPct={strip.completeness}
          reviewStatus={strip.review}
          reviewStatusKind={strip.reviewKind}
          publicationRule={publicationRuleLabel}
          publicationAudience={profileViewMode}
          loading={dashboardLoading}
          error={dashboardError}
        />
      </div>

      <ReportSection
        number={1}
        title="School & year"
        icon={FileText}
        auditBadge={sectionAuditBadgeFromReview(strip.reviewKind, strip.review)}
      >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {[
            { label: 'School name', value: school.name },
            { label: 'Code', value: school.code },
            { label: 'Region', value: school.region },
            { label: 'District', value: school.district },
          ].map((cell) => (
            <div
              key={cell.label}
              className="rounded-xl border border-slate-200/90 bg-gradient-to-b from-white to-slate-50/90 px-4 py-3 shadow-sm ring-1 ring-slate-100/80 print:border-slate-300"
            >
              <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500">{cell.label}</p>
              <p className="mt-1.5 text-sm font-semibold leading-snug text-slate-900">{cell.value}</p>
            </div>
          ))}
        </div>
      </ReportSection>

      <ReportSection
        number={2}
        title="Tuition (USD)"
        icon={Wallet}
        auditBadge={sectionAuditBadgeFromReview(strip.reviewKind, strip.review)}
      >
        <p className="mb-4 text-xs leading-relaxed text-slate-600 print:mb-2 print:text-[8px]">
          Tuition for the selected reporting year, using the school overview when available.
        </p>
        {dashboardLoading ? (
          <p className="text-sm font-medium text-slate-400">Loading…</p>
        ) : tuitionUsdForParent ? (
          <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm print:px-2 print:py-1.5">
            <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500">Tuition (USD)</p>
            <p className="mt-1 text-lg font-bold tabular-nums text-slate-900">
              {formatUsdWhole(tuitionUsdForParent.amount)}
            </p>
            <p className="mt-1 text-[10px] text-slate-500">{tuitionUsdForParent.subtitle}</p>
          </div>
        ) : (
          <p className="text-sm text-slate-600">No tuition figure is available for this school and year.</p>
        )}
      </ReportSection>

      <ReportSection
        number={3}
        title="Families like yours (peer comparison)"
        icon={Scale}
        auditBadge={sectionAuditBadgeFromReview(strip.reviewKind, strip.review)}
      >
        <p className="mb-4 text-sm leading-relaxed text-slate-600 print:mb-2 print:text-[8px]">
          Compares this school to others in a <strong>similar peer group</strong> (tuition and cost per student vs
          averages). <strong>N</strong> is the number of schools in that group.
        </p>
        {comparisonLoading ? (
          <p className="text-sm font-medium text-slate-500">Loading comparison…</p>
        ) : comparisonError ? (
          <p className="text-sm text-amber-800">{comparisonError}</p>
        ) : parentPeerGroup ? (
          <PeerGroupComparisonBlock
            group={parentPeerGroup}
            schoolTuition={peerChartSchoolTuition}
            schoolCost={peerChartSchoolCost}
            comparison={comparison}
          />
        ) : (
          <p className="text-sm text-slate-600">
            No peer comparison was returned for this school. Try another year, or contact the school if this persists.
          </p>
        )}
      </ReportSection>
    </div>
  );
}

function SchoolProfileLoadedView({
  school,
  year,
  reportingYearLabel,
  scopeLabel,
  publicationRuleLabel,
  profileViewMode,
  dashboard,
  dashboardLoading,
  dashboardError,
  comparison,
  comparisonLoading,
  comparisonError,
}: SchoolProfileLoadedViewProps) {
  const followUpsRef = useRef<SchoolFollowUpsSectionHandle | null>(null);
  const budgetMinResolved = useBudgetLawAbMinimumPercentResolved();
  const budgetWarnResolved = useBudgetLawAbWarningBandPercentResolved();
  const budgetSettingNotes = (
    <DashboardSettingSourceNotes resolved={[budgetMinResolved, budgetWarnResolved]} />
  );

  const strip = useMemo(() => {
    const d = dashboard;
    const useApi = Boolean(d) && !dashboardLoading;
    const fallbackV = mockData.governance.validationStatus;
    const fallbackC = mockData.governance.dataCompletenessPct;
    const fallbackR = mockData.governance.reviewStatus;

    const pickStr = (api: string | undefined, fb: string) => {
      if (!useApi) return fb;
      const t = api?.trim();
      return t ? t : '—';
    };

    const completeness =
      useApi && d?.dataCompleteness != null && Number.isFinite(Number(d.dataCompleteness))
        ? Math.min(100, Math.max(0, Math.round(Number(d.dataCompleteness))))
        : fallbackC;

    const budgetKindFallback = parseBudgetSubmittedStatus(fallbackV);
    const reviewKindFallback = parseReviewStatus(fallbackR);

    const validationKind = useApi
      ? (d?.budgetSubmittedStatusKind ?? parseBudgetSubmittedStatus(d?.budgetSubmittedStatus))
      : budgetKindFallback;
    const reviewKind = useApi
      ? (d?.reviewStatusKind ?? parseReviewStatus(d?.reviewStatus))
      : reviewKindFallback;

    const validation =
      useApi
        ? pickStr(d?.budgetSubmittedStatus, fallbackV)
        : budgetKindFallback !== null
          ? labelBudgetSubmittedStatus(budgetKindFallback)
          : fallbackV;
    const review =
      useApi
        ? pickStr(d?.reviewStatus, fallbackR)
        : reviewKindFallback !== null
          ? labelReviewStatus(reviewKindFallback)
          : fallbackR;

    return {
      validation,
      validationKind,
      completeness,
      review,
      reviewKind,
    };
  }, [dashboard, dashboardLoading]);

  const peerChartSchoolTuition = useMemo(() => {
    const c = comparison;
    if (c?.targetTuitionPerStudentUSD != null && Number.isFinite(c.targetTuitionPerStudentUSD)) {
      return c.targetTuitionPerStudentUSD;
    }
    const d = dashboard;
    if (d?.tuitionPerStudentUSD != null && Number.isFinite(d.tuitionPerStudentUSD)) {
      return d.tuitionPerStudentUSD;
    }
    return school.tuitionUsd;
  }, [comparison, dashboard, school.tuitionUsd]);

  const peerChartSchoolCost = useMemo(() => {
    const c = comparison;
    if (c?.targetCostPerStudentUSD != null && Number.isFinite(c.targetCostPerStudentUSD)) {
      return c.targetCostPerStudentUSD;
    }
    const d = dashboard;
    if (d?.costPerStudentUSD != null && Number.isFinite(d.costPerStudentUSD)) {
      return d.costPerStudentUSD;
    }
    return mockData.peer.costPerStudent.school;
  }, [comparison, dashboard]);

  const coreOperatingKpis = useMemo(() => {
    const d = dashboard;
    const useApi = Boolean(d) && !dashboardLoading;

    const students =
      useApi && d?.totalStudentCount != null && Number.isFinite(Number(d.totalStudentCount))
        ? Math.round(Number(d.totalStudentCount))
        : school.totalStudents;

    const teachers =
      useApi && d?.teacherCount != null && Number.isFinite(Number(d.teacherCount))
        ? Math.round(Number(d.teacherCount))
        : undefined;

    const ratioNum =
      useApi && d?.studentTeacherRatio != null && Number.isFinite(Number(d.studentTeacherRatio))
        ? Number(d.studentTeacherRatio)
        : undefined;

    const staffRatioNum =
      useApi && d?.studentStaffRatio != null && Number.isFinite(Number(d.studentStaffRatio))
        ? Number(d.studentStaffRatio)
        : undefined;

    const avgSalary =
      useApi && d?.averageTeacherSalaryUSD != null && Number.isFinite(Number(d.averageTeacherSalaryUSD))
        ? Number(d.averageTeacherSalaryUSD)
        : mockData.kpis.avgTeacherSalaryUsd;

    const ratioDisplay =
      ratioNum !== undefined ? formatStudentTeacherRatio(ratioNum) : mockData.kpis.studentTeacherRatio;
    const staffRatioDisplay = staffRatioNum !== undefined ? formatStudentTeacherRatio(staffRatioNum) : '—';

    /** Headcount implied by overview ratio so the subtitle matches the displayed "X : 1" value. */
    const teachersMatchingRatio =
      ratioNum != null && ratioNum > 0 && Number.isFinite(students)
        ? Math.max(1, Math.round(students / ratioNum))
        : undefined;

    /** Same idea as teachersMatchingRatio: subtitle must match the displayed student–staff ratio. */
    const staffMatchingRatio =
      staffRatioNum != null && staffRatioNum > 0 && Number.isFinite(students)
        ? Math.max(1, Math.round(students / staffRatioNum))
        : undefined;

    const teacherRatioSub =
      ratioNum !== undefined && ratioNum > 0 && teachersMatchingRatio !== undefined
        ? `${students.toLocaleString()} students ÷ ${teachersMatchingRatio.toLocaleString()} teachers`
        : 'Overview';

    const staffRatioSub =
      staffRatioNum !== undefined && staffMatchingRatio !== undefined
        ? `${students.toLocaleString()} students ÷ ${staffMatchingRatio.toLocaleString()} staff`
        : 'Overview';

    return [
      {
        key: 'salary',
        label: 'Avg. staff salary',
        value: `USD ${Math.round(avgSalary).toLocaleString()}`,
        sub: 'Average per month',
        emphasis: true,
      },
      {
        key: 'students',
        label: 'Total students',
        value: students.toLocaleString(),
        sub:
          teachers !== undefined
            ? `Enrollment · ${teachers.toLocaleString()} staff`
            : 'Enrollment on record',
        emphasis: false,
      },
      {
        key: 'teachers',
        label: 'Staff count',
        value: teachers !== undefined ? teachers.toLocaleString() : '—',
        sub: 'Permanent, contractual, and administrative',
        emphasis: true,
      },
      {
        key: 'teacher-ratio',
        label: 'Student-Teacher Ratio',
        value: ratioDisplay,
        sub: teacherRatioSub,
        emphasis: true,
      },
      {
        key: 'staff-ratio',
        label: 'Student-Staff Ratio',
        value: staffRatioDisplay,
        sub: staffRatioSub,
        emphasis: true,
      },
    ];
  }, [dashboard, dashboardLoading, school.totalStudents]);

  const salaryBreakdown = useMemo(() => {
    const d = dashboard;
    const useApi = Boolean(d) && !dashboardLoading;
    const pick = (n: number | undefined): number | undefined =>
      useApi && n != null && Number.isFinite(Number(n)) ? Number(n) : undefined;
    return {
      total: pick(d?.averageTeacherSalaryUSD),
      permanent: pick(d?.averagePermanentTeacherSalaryUSD),
      contractual: pick(d?.averageContractualTeacherSalaryUSD),
      admin: pick(d?.averageAdminTeacherSalaryUSD),
    };
  }, [dashboard, dashboardLoading]);

  const hasSalaryBreakdown =
    salaryBreakdown.permanent !== undefined ||
    salaryBreakdown.contractual !== undefined ||
    salaryBreakdown.admin !== undefined;

  const teacherCountBreakdown = useMemo(() => {
    const d = dashboard;
    const useApi = Boolean(d) && !dashboardLoading;
    const pick = (n: number | undefined): number | undefined =>
      useApi && n != null && Number.isFinite(Number(n)) ? Math.round(Number(n)) : undefined;
    return {
      total: pick(d?.teacherCount),
      permanent: pick(d?.permanentTeacherCount),
      contractual: pick(d?.contractualTeacherCount),
      admin: pick(d?.adminTeacherCount),
    };
  }, [dashboard, dashboardLoading]);

  const hasTeacherCountBreakdown =
    teacherCountBreakdown.permanent !== undefined ||
    teacherCountBreakdown.contractual !== undefined ||
    teacherCountBreakdown.admin !== undefined;

  const [salaryModalOpen, setSalaryModalOpen] = useState(false);
  const openSalaryModal = useCallback(() => setSalaryModalOpen(true), []);
  const closeSalaryModal = useCallback(() => setSalaryModalOpen(false), []);
  const [teacherCountModalOpen, setTeacherCountModalOpen] = useState(false);
  const openTeacherCountModal = useCallback(() => setTeacherCountModalOpen(true), []);
  const closeTeacherCountModal = useCallback(() => setTeacherCountModalOpen(false), []);
  const [tuitionByProgramOpen, setTuitionByProgramOpen] = useState(false);
  const openTuitionByProgramModal = useCallback(() => setTuitionByProgramOpen(true), []);
  const closeTuitionByProgramModal = useCallback(() => setTuitionByProgramOpen(false), []);

  const tuitionByProgramQuery = useClassTuitionByProgramQuery(school.id, year, {
    enabled: tuitionByProgramOpen && isDashboardSchoolId(school.id),
  });
  const tuitionByProgramErrMsg = tuitionByProgramQuery.isError
    ? classTuitionByProgramErrorMessage(tuitionByProgramQuery.error)
    : null;
  const canDrillTuitionPerStudent = isDashboardSchoolId(school.id);

  const budgetLawSplitProps = useMemo(() => {
    const d = dashboard;
    const useApi = Boolean(d) && !dashboardLoading;
    if (!useApi || !d) {
      return {
        abPercent: 0,
        cPercent: 0,
        abTotalUSD: 0,
        cTotalUSD: 0,
        compliant: false,
      };
    }
    return {
      abPercent: d.budgetSplit_AbPercent ?? 0,
      cPercent: d.budgetSplit_CPercent ?? 0,
      abTotalUSD: d.budgetSplit_AbTotalUSD ?? 0,
      cTotalUSD: d.budgetSplit_CTotalUSD ?? 0,
      compliant: d.budgetSplit_Compliant === true,
    };
  }, [dashboard, dashboardLoading]);

  const budgetMainBucketTotals = useMemo(() => {
    const d = dashboard;
    if (!d) {
      return { a: 0, b: 0, c: 0, d: 0 };
    }
    return {
      a: d.budgetSummaryATotalMainCurrency ?? 0,
      b: d.budgetSummaryBTotalMainCurrency ?? 0,
      c: d.budgetSummaryCTotalMainCurrency ?? 0,
      d: d.budgetSummaryDTotalMainCurrency ?? 0,
    };
  }, [dashboard]);

  const useMainBucketCompliancePanel = useMemo(() => {
    if (!dashboard || dashboardLoading) return false;
    const { a, b, c } = budgetMainBucketTotals;
    return a + b + c > 0;
  }, [budgetMainBucketTotals, dashboard, dashboardLoading]);

  const financialSummaryCards = useMemo(() => {
    const d = dashboard;
    const useApi = Boolean(d) && !dashboardLoading;
    const fb = mockData.financial;

    const pickUsd = (apiVal: number | undefined, sample: number): number | undefined => {
      if (useApi) {
        return apiVal != null && Number.isFinite(apiVal) ? apiVal : undefined;
      }
      return sample;
    };

    const tuitionRaw = pickUsd(
      d?.tuitionPerStudentUSD != null && Number.isFinite(Number(d.tuitionPerStudentUSD))
        ? Number(d.tuitionPerStudentUSD)
        : undefined,
      fb.tuitionPerStudentUsd
    );
    const tuition =
      tuitionRaw !== undefined && tuitionRaw > 0
        ? tuitionRaw
        : useApi && d
          ? school.tuitionUsd
          : tuitionRaw ?? fb.tuitionPerStudentUsd;

    const revenue = pickUsd(
      d?.totalRevenueUSD != null && Number.isFinite(Number(d.totalRevenueUSD))
        ? Number(d.totalRevenueUSD)
        : undefined,
      fb.totalRevenueUsd
    );
    const expenses = pickUsd(
      d?.totalExpensesUSD != null && Number.isFinite(Number(d.totalExpensesUSD))
        ? Number(d.totalExpensesUSD)
        : undefined,
      fb.totalExpensesUsd
    );
    const costPerStudent = pickUsd(
      d?.costPerStudentUSD != null && Number.isFinite(Number(d.costPerStudentUSD))
        ? Number(d.costPerStudentUSD)
        : undefined,
      fb.costPerStudentUsd
    );

    let surplus: number | undefined;
    if (useApi && d) {
      if (d.surplusUSD != null && Number.isFinite(Number(d.surplusUSD))) {
        surplus = Number(d.surplusUSD);
      } else if (revenue !== undefined && expenses !== undefined) {
        surplus = revenue - expenses;
      }
    } else {
      surplus = fb.totalRevenueUsd - fb.totalExpensesUsd;
    }

    const surplusTone =
      surplus === undefined
        ? 'neutral'
        : surplus > 0
          ? 'surplus'
          : surplus < 0
            ? 'deficit'
            : 'neutral';

    const fmt = (n: number | undefined) => (n !== undefined ? formatUsdWhole(n) : '—');

    return [
      {
        key: 'tuition-per-student',
        label: 'Tuition per student',
        value: fmt(tuition),
        sub: useApi ? "From the school's reported averages for the year" : 'Sample data',
        tone: 'neutral' as const,
      },
      {
        key: 'total-revenue',
        label: 'Total revenue',
        value: fmt(revenue),
        sub: 'School budget (USD)',
        tone: 'neutral' as const,
      },
      {
        key: 'total-expenses',
        label: 'Total expenses',
        value: fmt(expenses),
        sub: 'School budget (USD)',
        tone: 'neutral' as const,
      },
      {
        key: 'cost-per-student',
        label: 'Cost per student',
        value: fmt(costPerStudent),
        sub: 'From reported costs and enrollment',
        tone: 'neutral' as const,
      },
      {
        key: 'surplus',
        label: surplus !== undefined && surplus < 0 ? 'Deficit' : 'Surplus',
        value: fmt(surplus),
        sub:
          surplus !== undefined
            ? surplus < 0
              ? 'Expenses exceed revenue'
              : surplus > 0
                ? 'Revenue exceeds expenses'
                : 'Balanced'
            : useApi
              ? 'Needs reported revenue and expenses'
              : '—',
        tone: surplusTone,
      },
    ];
  }, [dashboard, dashboardLoading, school.tuitionUsd]);

  return (
    <div className="space-y-5 font-[Inter,ui-sans-serif,system-ui,sans-serif] print:space-y-1.5 print:text-[8px] print:leading-snug">
      <SchoolProfileHero school={school} reportingYearLabel={reportingYearLabel || year || '—'} />

      <div className="rounded-2xl border-2 border-slate-400/40 bg-slate-200/40 p-4 shadow-sm ring-1 ring-slate-300/50 print:border-slate-500 print:bg-slate-100 print:p-2 print:shadow-none">
        <GoverningStrip
          reportingYear={reportingYearLabel || year || '—'}
          scope={scopeLabel}
          validationStatus={strip.validation}
          validationStatusKind={strip.validationKind}
          dataCompletenessPct={strip.completeness}
          reviewStatus={strip.review}
          reviewStatusKind={strip.reviewKind}
          publicationRule={publicationRuleLabel}
          publicationAudience={profileViewMode}
          loading={dashboardLoading}
          error={dashboardError}
        />
      </div>

      <ReportSection
        number={1}
        title="Identity & status"
        icon={FileText}
        auditBadge={sectionAuditBadgeFromReview(strip.reviewKind, strip.review)}
      >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {[
            { label: 'School name', value: school.name },
            { label: 'Code', value: school.code },
            { label: 'Region', value: school.region },
            { label: 'District', value: school.district },
          ].map((cell) => (
            <div
              key={cell.label}
              className="group rounded-xl border border-slate-200/90 bg-gradient-to-b from-white to-slate-50/90 px-4 py-3 shadow-sm ring-1 ring-slate-100/80 transition hover:border-slate-300 hover:shadow-md print:border-slate-300"
            >
              <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500">{cell.label}</p>
              <p className="mt-1.5 text-sm font-semibold leading-snug text-slate-900">{cell.value}</p>
            </div>
          ))}
          <IdentityFieldShell label="Enrollment bands (classification)">
            <EnrollmentBandBadges
              schoolId={school.id}
              dashboard={dashboard}
              dashboardLoading={dashboardLoading}
              fallbackText={mockData.identity.enrollmentBand}
            />
          </IdentityFieldShell>
          <IdentityFieldShell label="Parents' committee & parent-facing tuition band">
            <ParentsBandStatus
              schoolId={school.id}
              dashboard={dashboard}
              dashboardLoading={dashboardLoading}
              fallbackCommitteeText={mockData.identity.parentsCommitteeStatus}
            />
          </IdentityFieldShell>
        </div>
      </ReportSection>

      <ReportSection
        number={2}
        title="Core operating profile (KPIs)"
        icon={Users}
        auditBadge={sectionAuditBadgeFromReview(strip.reviewKind, strip.review)}
      >
        <div className="grid grid-cols-2 items-stretch gap-3 sm:grid-cols-3 xl:grid-cols-5 print:grid-cols-5 print:gap-1">
          {coreOperatingKpis.map((kpi, i) => {
            const isSalary = kpi.key === 'salary';
            const isTeacherCount = kpi.key === 'teachers';
            const salaryClickable = isSalary && hasSalaryBreakdown;
            const teacherCountClickable = isTeacherCount && hasTeacherCountBreakdown;
            const clickable = salaryClickable || teacherCountClickable;
            const accent = KPI_ACCENT[i % KPI_ACCENT.length];
            if (kpi.emphasis) {
              const baseClass = `flex h-full min-h-0 flex-col rounded-xl border-2 border-slate-800/15 border-l-4 ${accent} bg-gradient-to-br from-white to-slate-50 px-4 py-4 shadow-md ring-1 ring-slate-200/80 print:border print:px-1.5 print:py-1.5 print:shadow-none`;
              const interactiveClass = clickable
                ? 'cursor-pointer text-left transition hover:-translate-y-0.5 hover:shadow-lg hover:ring-2 hover:ring-amber-300/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 print:cursor-default print:hover:translate-y-0 print:hover:shadow-none print:hover:ring-0'
                : '';
              const footerBreakdownChip = (label: string) => (
                <span className="mt-auto inline-flex items-center gap-1 rounded-full bg-amber-50 px-1.5 py-1 text-[8px] font-semibold uppercase tracking-wide text-amber-800 ring-1 ring-amber-200 print:hidden">
                  <Info className="size-2.5 shrink-0" aria-hidden /> {label}
                </span>
              );
              const salaryFooterLayout = salaryClickable && isSalary;
              const teacherFooterLayout = teacherCountClickable && isTeacherCount;
              const tileContent =
                salaryFooterLayout || teacherFooterLayout ? (
                  <>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 print:text-[6px]">
                      {kpi.label}
                    </p>
                    <p className="mt-2 text-2xl font-black tabular-nums tracking-tight text-slate-900 print:text-sm">
                      {kpi.value}
                    </p>
                    {salaryFooterLayout ? footerBreakdownChip('Breakdown') : null}
                    {teacherFooterLayout ? footerBreakdownChip('Breakdown') : null}
                  </>
                ) : (
                  <>
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 print:text-[6px]">
                        {kpi.label}
                      </p>
                    </div>
                    <p className="mt-2 text-2xl font-black tabular-nums tracking-tight text-slate-900 print:text-sm">
                      {kpi.value}
                    </p>
                    <p className="mt-1.5 text-[11px] font-medium leading-snug text-slate-600 print:text-[6px]">
                      {kpi.sub}
                    </p>
                  </>
                );
              return clickable ? (
                <button
                  key={kpi.key}
                  type="button"
                  onClick={salaryClickable ? openSalaryModal : openTeacherCountModal}
                  aria-label={salaryClickable ? 'Show average staff salary breakdown' : 'Show staff count breakdown'}
                  className={`${baseClass} ${interactiveClass}`}
                >
                  {tileContent}
                </button>
              ) : (
                <div key={kpi.key} className={baseClass}>
                  {tileContent}
                </div>
              );
            }
            return (
              <div
                key={kpi.key}
                className={`rounded-xl border border-slate-200/90 border-l-4 ${accent} bg-white px-3 py-3.5 shadow-sm ring-1 ring-slate-100 print:border-slate-400 print:px-1.5 print:py-1`}
              >
                <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500 print:text-[6px]">
                  {kpi.label}
                </p>
                <p className="mt-1 text-lg font-bold tabular-nums tracking-tight text-slate-900 print:text-[10px]">
                  {kpi.value}
                </p>
                <p className="mt-1 text-[10px] font-medium text-slate-500 print:text-[6px]">{kpi.sub}</p>
              </div>
            );
          })}
        </div>
      </ReportSection>

      <ReportSection
        number={3}
        title="Financial summary"
        icon={Wallet}
        auditBadge={sectionAuditBadgeFromReview(strip.reviewKind, strip.review)}
      >
        <p className="mb-4 text-xs leading-relaxed text-slate-600 print:mb-1 print:text-[8px]">
          Key financial figures for the reporting year (USD, rounded). The chart below summarizes the spending
          split checked by the {budgetMinResolved.displayValue} rule.
        </p>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-5 print:grid-cols-5 print:gap-1">
          {financialSummaryCards.map((row, i) => {
            const accent =
              row.tone === 'deficit'
                ? 'border-l-rose-500 ring-rose-100'
                : row.tone === 'surplus'
                  ? 'border-l-emerald-500 ring-emerald-100'
                  : `${FIN_SUMMARY_ACCENT[i % FIN_SUMMARY_ACCENT.length]}`;
            const valueClass = `mt-1 text-lg font-bold tabular-nums tracking-tight print:text-[9px] ${
              row.tone === 'deficit'
                ? 'text-rose-700'
                : row.tone === 'surplus'
                  ? 'text-emerald-700'
                  : 'text-slate-900'
            }`;
            const isTuitionPerStudent = row.key === 'tuition-per-student';
            const tuitionClickable = isTuitionPerStudent && canDrillTuitionPerStudent;
            const baseClass = `rounded-xl border border-slate-200/90 border-l-4 bg-white px-3 py-3.5 shadow-sm ring-1 ring-slate-100 print:border-slate-400 print:px-1.5 print:py-1 ${accent}`;
            const interactiveClass = tuitionClickable
              ? 'cursor-pointer text-left transition hover:-translate-y-0.5 hover:shadow-md hover:ring-2 hover:ring-sky-300/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 print:cursor-default print:hover:translate-y-0 print:hover:shadow-none print:hover:ring-0'
              : '';
            const cardContent = (
              <>
                <div className="flex items-start justify-between gap-2">
                  <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500 print:text-[6px]">
                    {row.label}
                  </p>
                  {tuitionClickable && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-1.5 py-0.5 text-[8px] font-semibold uppercase tracking-wide text-sky-800 ring-1 ring-sky-200 print:hidden">
                      <Info className="size-2.5" aria-hidden /> Programs
                    </span>
                  )}
                </div>
                <p className={valueClass}>{row.value}</p>
                {!tuitionClickable ? (
                  <p className="mt-1 text-[10px] font-medium text-slate-500 print:text-[6px]">{row.sub}</p>
                ) : null}
              </>
            );
            if (tuitionClickable) {
              return (
                <button
                  key={row.key}
                  type="button"
                  onClick={openTuitionByProgramModal}
                  aria-label="Show tuition per student breakdown by program"
                  className={`${baseClass} ${interactiveClass}`}
                >
                  {cardContent}
                </button>
              );
            }
            return (
              <div key={row.key} className={baseClass}>
                {cardContent}
              </div>
            );
          })}
        </div>
        <h3 className="mb-2 mt-8 text-[11px] font-bold uppercase tracking-wide text-slate-600 print:mt-3 print:text-[8px]">
          Legal cost-bucket mix ({budgetMinResolved.displayValue} rule)
        </h3>
        {useMainBucketCompliancePanel ? (
          <BudgetCompliancePanel
            BudgetSummary_ATotalMainCurruncy={budgetMainBucketTotals.a}
            BudgetSummary_BTotalMainCurruncy={budgetMainBucketTotals.b}
            BudgetSummary_CTotalMainCurruncy={budgetMainBucketTotals.c}
            BudgetSummary_DTotalMainCurruncy={budgetMainBucketTotals.d}
            threshold={budgetMinResolved.value / 100}
            minimumPercent={budgetMinResolved.value}
            totalRevenueUSD={dashboard?.totalRevenueUSD}
            totalExpensesUSD={dashboard?.totalExpensesUSD}
            settingSourceNote={budgetSettingNotes}
          />
        ) : (
          <BudgetLawSplit
            abPercent={budgetLawSplitProps.abPercent}
            cPercent={budgetLawSplitProps.cPercent}
            abTotalUSD={budgetLawSplitProps.abTotalUSD}
            cTotalUSD={budgetLawSplitProps.cTotalUSD}
            compliant={budgetLawSplitProps.compliant}
            currency="USD"
            minimumFraction={budgetMinResolved.value / 100}
            warningFraction={budgetWarnResolved.value / 100}
            settingSourceNote={budgetSettingNotes}
          />
        )}
      </ReportSection>

      <ReportSection
        number={4}
        title="Peer comparison"
        icon={Scale}
        auditBadge={sectionAuditBadgeFromReview(strip.reviewKind, strip.review)}
      >
        <PeerComparisonSection
          schoolId={school.id}
          schoolName={school.name}
          year={year}
          reportingYearLabel={reportingYearLabel}
          comparison={comparison}
          comparisonLoading={comparisonLoading}
          comparisonError={comparisonError}
          chartSchoolTuition={peerChartSchoolTuition}
          chartSchoolCost={peerChartSchoolCost}
        />
      </ReportSection>

      <ReportSection
        number={5}
        title="Follow-ups"
        icon={ClipboardList}
        className="print:break-inside-auto"
        headerActions={
          <Button
            type="button"
            variant="primary"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs"
            onClick={() => followUpsRef.current?.openAdd()}
          >
            <Plus className="size-3.5 shrink-0" aria-hidden />
            {ADD_COMPLIANCE_ISSUE_LABEL}
          </Button>
        }
      >
        <SchoolFollowUpsSection ref={followUpsRef} schoolId={school.id} year={year} />
      </ReportSection>

      <SalaryBreakdownModal
        open={salaryModalOpen}
        onClose={closeSalaryModal}
        breakdown={salaryBreakdown}
        teacherCount={dashboard?.teacherCount}
      />
      <TeacherCountBreakdownModal
        open={teacherCountModalOpen}
        onClose={closeTeacherCountModal}
        breakdown={teacherCountBreakdown}
      />
      <TuitionByProgramModal
        open={tuitionByProgramOpen}
        onClose={closeTuitionByProgramModal}
        rows={tuitionByProgramQuery.data}
        loading={tuitionByProgramQuery.isLoading || tuitionByProgramQuery.isFetching}
        error={tuitionByProgramErrMsg}
        reportingYearLabel={reportingYearLabel || year || ''}
        schoolName={school.name}
        schoolTuitionPerStudentUSD={dashboard?.tuitionPerStudentUSD}
        totalStudents={dashboard?.totalStudentCount ?? school.totalStudents}
      />
    </div>
  );
}

interface SalaryBreakdownModalProps {
  open: boolean;
  onClose: () => void;
  breakdown: {
    total?: number;
    permanent?: number;
    contractual?: number;
    admin?: number;
  };
  teacherCount?: number;
}

function SalaryBreakdownModal({
  open,
  onClose,
  breakdown,
  teacherCount,
}: SalaryBreakdownModalProps) {
  const fmt = (n: number | undefined): string =>
    n != null && Number.isFinite(n) ? `USD ${Math.round(n).toLocaleString()}` : '—';

  const rows: Array<{
    key: string;
    label: string;
    description: string;
    value: number | undefined;
    accent: string;
  }> = [
    {
      key: 'permanent',
      label: 'Permanent teachers',
      description: 'Average monthly pay for permanent teaching staff.',
      value: breakdown.permanent,
      accent: 'border-l-emerald-500 bg-emerald-50/40',
    },
    {
      key: 'contractual',
      label: 'Contractual teachers',
      description: 'Average monthly pay for contractual teaching staff.',
      value: breakdown.contractual,
      accent: 'border-l-sky-500 bg-sky-50/40',
    },
    {
      key: 'admin',
      label: 'Administrative staff',
      description: 'Average monthly pay for administrative (non-teaching) staff.',
      value: breakdown.admin,
      accent: 'border-l-violet-500 bg-violet-50/40',
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={560}
      destroyOnClose
      maskClosable
      title={
        <span className="text-base font-semibold text-slate-900">Avg. staff salary — breakdown</span>
      }
    >
      <div className="space-y-4 pt-1 text-slate-800">
        <div className="rounded-lg border border-amber-200 bg-amber-50/70 px-3 py-2.5">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-800">
            Overall average · per month
          </p>
          <p className="mt-1 text-2xl font-black tabular-nums tracking-tight text-slate-900">
            {fmt(breakdown.total)}
          </p>
          <p className="mt-0.5 text-xs text-slate-700">
            {teacherCount != null && Number.isFinite(teacherCount) && teacherCount > 0
              ? `Across ${teacherCount.toLocaleString()} staff (permanent, contractual, and administrative roles).`
              : 'Across all reported staff roles.'}
          </p>
        </div>

        <ul className="space-y-2.5">
          {rows.map((row) => (
            <li
              key={row.key}
              className={`rounded-lg border border-slate-200 border-l-4 ${row.accent} px-3 py-2.5 shadow-sm`}
            >
              <div className="flex items-baseline justify-between gap-3">
                <p className="text-sm font-semibold text-slate-900">{row.label}</p>
                <p className="text-lg font-bold tabular-nums text-slate-900">{fmt(row.value)}</p>
              </div>
              <p className="mt-0.5 text-[11px] leading-snug text-slate-600">{row.description}</p>
            </li>
          ))}
        </ul>
      </div>
    </Modal>
  );
}

interface TeacherCountBreakdownModalProps {
  open: boolean;
  onClose: () => void;
  breakdown: {
    total?: number;
    permanent?: number;
    contractual?: number;
    admin?: number;
  };
}

function TeacherCountBreakdownModal({
  open,
  onClose,
  breakdown,
}: TeacherCountBreakdownModalProps) {
  const fmt = (n: number | undefined): string =>
    n != null && Number.isFinite(n) ? Math.round(n).toLocaleString() : '—';

  const rows: Array<{
    key: string;
    label: string;
    description: string;
    value: number | undefined;
    accent: string;
  }> = [
    {
      key: 'permanent',
      label: 'Permanent teachers',
      description: 'Permanent teaching staff count.',
      value: breakdown.permanent,
      accent: 'border-l-emerald-500 bg-emerald-50/40',
    },
    {
      key: 'contractual',
      label: 'Contractual teachers',
      description: 'Contracted teaching staff count.',
      value: breakdown.contractual,
      accent: 'border-l-sky-500 bg-sky-50/40',
    },
    {
      key: 'admin',
      label: 'Administrative staff',
      description: 'Administrative staff count.',
      value: breakdown.admin,
      accent: 'border-l-violet-500 bg-violet-50/40',
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={560}
      destroyOnClose
      maskClosable
      title={<span className="text-base font-semibold text-slate-900">Staff count breakdown</span>}
    >
      <div className="space-y-4 pt-1 text-slate-800">
        <div className="rounded-lg border border-amber-200 bg-amber-50/70 px-3 py-2.5">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-800">
            Total staff
          </p>
          <p className="mt-1 text-2xl font-black tabular-nums tracking-tight text-slate-900">
            {fmt(breakdown.total)}
          </p>
          <p className="mt-0.5 text-xs text-slate-700">
            Includes permanent teachers, contractual teachers, and administrative staff.
          </p>
        </div>

        <ul className="space-y-2.5">
          {rows.map((row) => (
            <li
              key={row.key}
              className={`rounded-lg border border-slate-200 border-l-4 ${row.accent} px-3 py-2.5 shadow-sm`}
            >
              <div className="flex items-baseline justify-between gap-3">
                <p className="text-sm font-semibold text-slate-900">{row.label}</p>
                <p className="text-lg font-bold tabular-nums text-slate-900">{fmt(row.value)}</p>
              </div>
              <p className="mt-0.5 text-[11px] leading-snug text-slate-600">{row.description}</p>
            </li>
          ))}
        </ul>
      </div>
    </Modal>
  );
}

interface TuitionByProgramModalProps {
  open: boolean;
  onClose: () => void;
  rows: ClassTuitionByProgramRow[] | undefined;
  loading: boolean;
  error: string | null;
  reportingYearLabel: string;
  schoolName: string;
  schoolTuitionPerStudentUSD?: number;
  totalStudents?: number;
}

const PROGRAM_ACCENTS = [
  'border-l-emerald-500 bg-emerald-50/40',
  'border-l-sky-500 bg-sky-50/40',
  'border-l-violet-500 bg-violet-50/40',
  'border-l-amber-500 bg-amber-50/40',
  'border-l-rose-500 bg-rose-50/40',
  'border-l-teal-500 bg-teal-50/40',
] as const;

/**
 * Lebanese curriculum order (youngest → oldest):
 * Kindergarten (روضة) → Basic / Elementary (اساسي) → Intermediate (متوسط) → Secondary (ثانوي).
 * Matches Arabic + common Latin spellings; unknown titles fall back after these,
 * preserving the API's relative order via a stable sort.
 */
function lebanonProgramOrder(title: string): number {
  const t = (title ?? '').trim().toLowerCase();
  if (!t) return 99;
  if (/(روض)|(kg)|(kinder)|(pre[-\s]?school)|(nursery)/i.test(t)) return 0;
  if (/(اساس)|(أساس)|(اساسي)|(basic)|(elementary)|(primary)/i.test(t)) return 1;
  if (/(متوسط)|(intermediate)|(middle)|(prep)/i.test(t)) return 2;
  if (/(ثانو)|(secondary)|(high)/i.test(t)) return 3;
  return 99;
}

function sortByLebanonProgramOrder(
  rows: ClassTuitionByProgramRow[]
): ClassTuitionByProgramRow[] {
  return rows
    .map((r, i) => ({ r, i, ord: lebanonProgramOrder(r.classProgramTitle) }))
    .sort((a, b) => (a.ord !== b.ord ? a.ord - b.ord : a.i - b.i))
    .map((x) => x.r);
}

function TuitionByProgramModal({
  open,
  onClose,
  rows,
  loading,
  error,
  reportingYearLabel,
  schoolName,
  schoolTuitionPerStudentUSD,
  totalStudents,
}: TuitionByProgramModalProps) {
  const fmtUsd = (n: number | undefined): string =>
    n != null && Number.isFinite(n) ? `USD ${Math.round(n).toLocaleString()}` : '—';
  const fmtCount = (n: number | undefined): string =>
    n != null && Number.isFinite(n) ? Math.round(n).toLocaleString() : '—';

  const orderedRows = useMemo(
    () => (rows ? sortByLebanonProgramOrder(rows) : []),
    [rows]
  );

  const computed = useMemo(() => {
    const totalTuition = orderedRows.reduce(
      (s, r) => s + (Number.isFinite(r.totalTuition) ? r.totalTuition : 0),
      0
    );
    const totalStudentsAcrossPrograms = orderedRows.reduce(
      (s, r) => s + (Number.isFinite(r.numberOfStudents) ? r.numberOfStudents : 0),
      0
    );
    const weightedAvg =
      totalStudentsAcrossPrograms > 0 ? totalTuition / totalStudentsAcrossPrograms : undefined;
    return { totalTuition, totalStudentsAcrossPrograms, weightedAvg };
  }, [orderedRows]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={720}
      destroyOnClose
      maskClosable
      title={
        <span className="text-base font-semibold text-slate-900">
          Tuition per student — by program
        </span>
      }
    >
      <div className="space-y-4 pt-1 text-slate-800">
        <div className="rounded-lg border border-sky-200 bg-sky-50/70 px-3 py-2.5">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-sky-800">
            School overview · {reportingYearLabel || '—'}
          </p>
          <p className="mt-1 text-sm font-semibold text-slate-900">{schoolName || '—'}</p>
          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-slate-700">
            <span>
              Tuition / student:{' '}
              <span className="font-bold tabular-nums">{fmtUsd(schoolTuitionPerStudentUSD)}</span>
            </span>
            <span>
              Total students:{' '}
              <span className="font-bold tabular-nums">{fmtCount(totalStudents)}</span>
            </span>
          </div>
        </div>

        {loading ? (
          <p className="text-sm font-medium text-slate-500">Loading tuition by program…</p>
        ) : error ? (
          <p className="text-sm text-amber-800">{error}</p>
        ) : !rows || rows.length === 0 ? (
          <p className="text-sm text-slate-600">
            No program-level tuition data was returned for this school and year.
          </p>
        ) : (
          <>
            <div className="overflow-hidden rounded-xl border border-slate-200 shadow-sm ring-1 ring-slate-100">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[480px] border-collapse text-left text-sm">
                  <thead>
                    <tr className="bg-slate-100/90">
                      <th className="border-b border-slate-200 px-3 py-2 text-[11px] font-bold uppercase tracking-wide text-slate-700">
                        Program
                      </th>
                      <th className="border-b border-slate-200 px-3 py-2 text-right text-[11px] font-bold uppercase tracking-wide text-slate-700">
                        Students
                      </th>
                      <th className="border-b border-slate-200 px-3 py-2 text-right text-[11px] font-bold uppercase tracking-wide text-slate-700">
                        Total tuition
                      </th>
                      <th className="border-b border-slate-200 px-3 py-2 text-right text-[11px] font-bold uppercase tracking-wide text-slate-700">
                        Tuition / student
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {orderedRows.map((r, i) => {
                      const accent = PROGRAM_ACCENTS[i % PROGRAM_ACCENTS.length];
                      return (
                        <tr key={`${r.classProgramTitle}-${i}`} className="bg-white">
                          <td
                            className={`border-b border-slate-100 px-3 py-2 align-middle border-l-4 font-semibold text-slate-900 ${accent}`}
                          >
                            {r.classProgramTitle?.trim() || '—'}
                          </td>
                          <td className="border-b border-slate-100 px-3 py-2 text-right tabular-nums text-slate-800">
                            {fmtCount(r.numberOfStudents)}
                          </td>
                          <td className="border-b border-slate-100 px-3 py-2 text-right tabular-nums text-slate-800">
                            {fmtUsd(r.totalTuition)}
                          </td>
                          <td className="border-b border-slate-100 px-3 py-2 text-right font-bold tabular-nums text-slate-900">
                            {fmtUsd(r.tuitionPerStudent)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-slate-50">
                      <td className="px-3 py-2 text-[11px] font-bold uppercase tracking-wide text-slate-700">
                        All programs
                      </td>
                      <td className="px-3 py-2 text-right font-bold tabular-nums text-slate-900">
                        {fmtCount(computed.totalStudentsAcrossPrograms)}
                      </td>
                      <td className="px-3 py-2 text-right font-bold tabular-nums text-slate-900">
                        {fmtUsd(computed.totalTuition)}
                      </td>
                      <td className="px-3 py-2 text-right font-bold tabular-nums text-slate-900">
                        {fmtUsd(computed.weightedAvg)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
            <p className="text-[11px] leading-relaxed text-slate-500">
              Each program row shows students, total tuition, and tuition per student. The all programs row is a
              weighted average. Amounts are rounded to whole USD.
            </p>
          </>
        )}
      </div>
    </Modal>
  );
}

export function SchoolProfileDashboardPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const deepLink = useMemo(
    () => readSchoolProfileDeepLink(searchParams.toString()),
    [searchParams]
  );
  const isSchoolAdmin = useMemo(
    () => userHasRoleCaseInsensitive(user?.roles, 'SchoolAdmin'),
    [user?.roles]
  );

  const defaultYear = useDefaultYear();
  const [governorateId, setGovernorateId] = useState(deepLink.governorateId);
  const [governorateLabel, setGovernorateLabel] = useState('');
  const [selectedSchoolId, setSelectedSchoolId] = useState('');
  const [selectedSchoolLabel, setSelectedSchoolLabel] = useState('');
  const [selectedSchoolDetail, setSelectedSchoolDetail] = useState<SchoolWithFiltersItemDto | null>(null);
  const [year, setYear] = useState(deepLink.year);
  const [yearLabel, setYearLabel] = useState(deepLink.year);

  useEffect(() => {
    if (!year && defaultYear) {
      setYear(defaultYear);
      setYearLabel(defaultYear);
    }
  }, [defaultYear, year]);

  const schoolOptionsCacheRef = useRef<Map<string, SchoolWithFiltersItemDto>>(new Map());
  const deepLinkLoadedRef = useRef<string | null>(null);

  /** Open school + year from query string (e.g. Group Comparisons → new tab). */
  useEffect(() => {
    const sid = deepLink.schoolId;
    const yStr = deepLink.year;
    if (!sid || !yStr || !/^\d+$/.test(yStr)) return;

    const linkKey = `${sid}:${yStr}:${deepLink.governorateId}`;
    if (deepLinkLoadedRef.current === linkKey && selectedSchoolDetail?.id === sid) return;

    let cancelled = false;
    (async () => {
      const yNum = parseInt(yStr, 10);
      const govNum = deepLink.governorateId ? parseInt(deepLink.governorateId, 10) : NaN;

      try {
        const res = await schoolWithFiltersApi.postList({
          schoolIds: [sid],
          year: yNum,
          governorateId: Number.isFinite(govNum) ? govNum : undefined,
          maxResultCount: 5,
        });
        if (cancelled) return;

        const school = res.items.find((it) => it.id === sid) ?? res.items[0];
        if (!school?.id) return;

        deepLinkLoadedRef.current = linkKey;
        schoolOptionsCacheRef.current.set(school.id, school);
        setYear(yStr);
        setYearLabel(yStr);
        setSelectedSchoolId(school.id);
        setSelectedSchoolLabel(formatSchoolApiLabel(school));
        setSelectedSchoolDetail(school);

        if (Number.isFinite(govNum)) {
          setGovernorateId(String(govNum));
          setGovernorateLabel(school.governorateName?.trim() ?? '');
        }
      } catch {
        /* User can still pick filters manually. */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [deepLink, selectedSchoolDetail?.id]);

  const fetchGovernorates = useCallback(async (search: string): Promise<SearchableOption[]> => {
    const data = await regionApi.getGovernorates({
      filter: search || undefined,
    });
    return (data ?? []).map((item) => ({
      id: String(item.id),
      label: item.name,
    }));
  }, []);

  const fetchBudgetYears = useCallback(async (search: string): Promise<SearchableOption[]> => {
    const data = await budgetsApi.getBudgetYears({
      skipCount: 0,
      maxResultCount: 100,
      year: search || undefined,
    });
    return (data ?? []).map((item) => ({
      id: String(item),
      label: String(item),
    }));
  }, []);

  const fetchSchoolsMinistry = useCallback(
    async (search: string): Promise<SearchableOption[]> => {
      if (!isSchoolAdmin) {
        if (!governorateId) return [];
        const govNum = parseInt(governorateId, 10);
        if (!Number.isFinite(govNum)) return [];
      }
      const yTrim = year.trim();
      if (!yTrim || !/^\d+$/.test(yTrim)) return [];
      const yearNum = parseInt(yTrim, 10);
      if (!Number.isFinite(yearNum) || yearNum <= 0) return [];
      const govNum = governorateId ? parseInt(governorateId, 10) : NaN;
      const res = await schoolWithFiltersApi.getList({
        governorateId: Number.isFinite(govNum) ? govNum : undefined,
        year: yearNum,
        keyword: search.trim() || undefined,
        skipCount: 0,
        maxResultCount: 80,
      });
      for (const it of res.items) {
        if (it.id) schoolOptionsCacheRef.current.set(it.id, it);
      }
      return res.items
        .filter((it) => it.id)
        .map((it) => ({
          id: it.id,
          label: formatSchoolApiLabel(it),
        }));
    },
    [governorateId, year, isSchoolAdmin]
  );

  const handleGovernorateChange = useCallback((id: string, label: string) => {
    setGovernorateId(id);
    setGovernorateLabel(label);
    setSelectedSchoolId('');
    setSelectedSchoolLabel('');
    setSelectedSchoolDetail(null);
    schoolOptionsCacheRef.current.clear();
  }, []);

  const handleSchoolSelect = useCallback((id: string, label: string) => {
    setSelectedSchoolId(id);
    setSelectedSchoolLabel(label);
    if (id) {
      setSelectedSchoolDetail(schoolOptionsCacheRef.current.get(id) ?? null);
    } else {
      setSelectedSchoolDetail(null);
    }
  }, []);

  const handleYearSelect = useCallback((id: string, label: string) => {
    setYear((prev) => {
      if (prev !== id) {
        setSelectedSchoolId('');
        setSelectedSchoolLabel('');
        setSelectedSchoolDetail(null);
        schoolOptionsCacheRef.current.clear();
      }
      return id;
    });
    setYearLabel(label);
  }, []);

  /** One school per school admin from API — auto-select so they usually only pick the year. */
  useEffect(() => {
    if (!isSchoolAdmin) return;
    const yTrim = year.trim();
    if (!yTrim || !/^\d+$/.test(yTrim)) return;
    if (selectedSchoolId) return;
    const yNum = parseInt(yTrim, 10);
    if (!Number.isFinite(yNum) || yNum <= 0) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await schoolWithFiltersApi.getList({
          year: yNum,
          skipCount: 0,
          maxResultCount: 80,
        });
        if (cancelled) return;
        const withIds = res.items.filter((it) => it.id);
        for (const it of withIds) {
          schoolOptionsCacheRef.current.set(it.id, it);
        }
        if (withIds.length !== 1) return;
        const only = withIds[0]!;
        setSelectedSchoolId(only.id);
        setSelectedSchoolLabel(formatSchoolApiLabel(only));
        setSelectedSchoolDetail(only);
      } catch {
        /* user can still search in the school field */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isSchoolAdmin, year, selectedSchoolId]);

  const activeSchool = useMemo((): ActiveSchoolCard | null => {
    if (!year || !selectedSchoolId) return null;
    if (!isSchoolAdmin && !governorateId) return null;
    const d = selectedSchoolDetail;
    if (!d?.id || d.id !== selectedSchoolId) return null;
    const districtParts = [d.cityName?.trim(), d.areaName?.trim()].filter(Boolean);
    return {
      id: d.id,
      name: d.schoolName?.trim() || '—',
      code: d.code?.trim() || d.educationalCenterNumber?.trim() || '—',
      region: d.governorateName?.trim() || governorateLabel || '—',
      district: districtParts.length ? districtParts.join(' · ') : '—',
      totalStudents: d.totalStudentCount ?? 612,
      tuitionUsd:
        typeof d.tuitionUSD === 'number' && Number.isFinite(d.tuitionUSD) && d.tuitionUSD > 0
          ? d.tuitionUSD
          : mockData.peer.tuition.school,
    };
  }, [year, selectedSchoolId, governorateId, governorateLabel, selectedSchoolDetail, isSchoolAdmin]);

  const scopeLabel = useMemo(
    () => formatAudienceScope(user?.roles, governorateLabel),
    [user?.roles, governorateLabel]
  );

  const yearDigits = year.trim() && /^\d+$/.test(year.trim()) ? year.trim() : undefined;
  const bandsGate = useSchoolBandsConfiguredForYear(yearDigits);
  const classificationWorkflowReady =
    yearDigits == null ? undefined : bandsGate.isSuccess && bandsGate.configured ? true : false;
  const showProfileBody = Boolean(yearDigits && bandsGate.isSuccess && bandsGate.configured);

  const profileQuery = useSchoolClassificationComparisonQuery(activeSchool?.id, year, {
    classificationWorkflowReady,
  });
  const profileErrorMessage = profileQuery.isError
    ? schoolClassificationComparisonErrorMessage(profileQuery.error)
    : null;

  const dashboardFromProfile = profileQuery.data?.dashboard;

  const publicationRuleLabel = useMemo(
    () => getSchoolProfilePublicationRuleLabel(user?.roles),
    [user?.roles]
  );

  const profileViewMode = useMemo(() => getSchoolProfileViewMode(user?.roles), [user?.roles]);
  const isParentPortal = profileViewMode === 'parent';

  const handlePrintPdf = useCallback(() => {
    message.info('Print to PDF (demo): layout prepared for export. Backend wiring will follow.');
  }, []);

  return (
    <div className="school-profile-print-root min-h-0 bg-gradient-to-b from-slate-200/50 via-slate-50 to-slate-100/80 pb-10 font-[Inter,ui-sans-serif,system-ui,sans-serif] text-slate-900 antialiased">
      <header className="sticky top-0 z-30 border-b border-slate-200/90 bg-slate-100/90 shadow-sm backdrop-blur-md supports-[backdrop-filter]:bg-slate-100/85">
        <div className="mx-auto max-w-6xl px-4 py-3 lg:px-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 items-center gap-3 text-slate-800">
              <span className="flex size-10 shrink-0 items-center justify-center rounded-2xl bg-slate-900 text-white shadow-md ring-1 ring-slate-900/15">
                <ShieldCheck className="size-[18px]" aria-hidden />
              </span>
              <div className="min-w-0">
                <h1 className="text-xl font-bold tracking-tight text-slate-900">
                  {isParentPortal ? 'School profile (parents)' : 'School Profile Dashboard'}
                </h1>
                {isSchoolAdmin ? (
                  <p className="mt-0.5 text-xs leading-snug text-slate-600">
                    Ministry roles browse by governorate; your account is scoped to your school only.
                  </p>
                ) : isParentPortal ? (
                  <p className="mt-0.5 text-xs leading-snug text-violet-900">
                    Tuition in USD and a simple peer comparison — not the full ministry school profile.
                  </p>
                ) : null}
              </div>
            </div>
            <button
              type="button"
              onClick={handlePrintPdf}
              className="inline-flex shrink-0 items-center justify-center gap-2 self-start rounded-xl border border-slate-800 bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white shadow-md transition hover:bg-slate-800 hover:shadow-lg active:scale-[0.99] sm:self-auto"
            >
              <Printer className="size-4" aria-hidden />
              Print to PDF
            </button>
          </div>

          <div className="mt-5 w-full rounded-2xl border border-slate-200/90 bg-white p-5 shadow-sm ring-1 ring-slate-100/80">
              <div className="mb-4 flex flex-wrap items-end justify-between gap-3 border-b border-slate-100 pb-4">
                <div>
                  <h2 className="text-sm font-semibold text-slate-900">Report filters</h2>
                  <p className="mt-1 max-w-xl text-xs leading-relaxed text-slate-500">
                    {isParentPortal
                      ? 'Choose governorate, reporting year, and school. You will see tuition in USD and a peer comparison—not enrollment or governed tuition tables.'
                      : isSchoolAdmin
                        ? 'Choose reporting year first. Your school list comes from your account; when only one school is returned it is selected for you.'
                        : 'Choose governorate and reporting year first, then search for a school. The profile appears when all are set.'}
                  </p>
                </div>
                <p className="text-xs text-slate-400">
                  <span className="text-red-600">*</span> Reporting year required
                </p>
              </div>
              <div
                className={`grid w-full gap-4 ${isSchoolAdmin ? 'sm:grid-cols-2' : 'sm:grid-cols-2 lg:grid-cols-3'}`}
              >
                {!isSchoolAdmin ? (
                  <div className="min-w-0 w-full">
                    <label className="mb-2 flex items-center gap-2 text-xs font-semibold text-slate-700">
                      <MapPinIcon className="size-3.5 text-slate-400" aria-hidden />
                      Governorate
                    </label>
                    <SearchableSelect
                      value={governorateId}
                      onSelect={handleGovernorateChange}
                      fetchOptions={fetchGovernorates}
                      placeholder="Search governorate…"
                      selectedLabel={governorateLabel}
                      clearable
                      className="min-w-0 w-full"
                    />
                  </div>
                ) : null}

                <div className="min-w-0 w-full">
                  <label className="mb-2 flex items-center gap-2 text-xs font-semibold text-slate-700">
                    <CalendarIcon className="size-3.5 text-slate-400" aria-hidden />
                    Reporting year <span className="text-red-600">*</span>
                  </label>
                  <SearchableSelect
                    value={year}
                    onSelect={handleYearSelect}
                    fetchOptions={fetchBudgetYears}
                    placeholder="Select year…"
                    selectedLabel={yearLabel || year}
                    clearable={false}
                    className="min-w-0 w-full"
                  />
                </div>

                <div className="min-w-0 w-full">
                  <label className="mb-2 flex items-center gap-2 text-xs font-semibold text-slate-700">
                    <Building2 className="size-3.5 text-slate-400" aria-hidden />
                    {isSchoolAdmin ? 'Your school' : 'School'}
                  </label>
                  <div
                    className={
                      !year.trim() ||
                      !/^\d+$/.test(year.trim()) ||
                      (!isSchoolAdmin && !governorateId)
                        ? 'opacity-60'
                        : ''
                    }
                  >
                    <SearchableSelect
                      value={selectedSchoolId}
                      onSelect={handleSchoolSelect}
                      fetchOptions={fetchSchoolsMinistry}
                      placeholder={
                        !year.trim() || !/^\d+$/.test(year.trim())
                          ? 'Select reporting year first…'
                          : !isSchoolAdmin && !governorateId
                            ? 'Select governorate first…'
                            : isSchoolAdmin
                              ? 'Search or confirm your school…'
                              : 'Search by name or code…'
                      }
                      selectedLabel={selectedSchoolLabel}
                      clearable
                      disabled={
                        !year.trim() ||
                        !/^\d+$/.test(year.trim()) ||
                        (!isSchoolAdmin && !governorateId)
                      }
                      className="min-w-0 w-full"
                    />
                  </div>
                </div>
              </div>
            </div>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-4 py-6 lg:px-6">
        {yearDigits && bandsGate.isPending ? (
          <p className="mb-6 text-center text-sm text-slate-600">Checking classification setup for this year…</p>
        ) : null}
        {yearDigits && bandsGate.isError ? (
          <Alert
            type="error"
            showIcon
            className="mb-6"
            message="Could not verify classification workflow"
            description={
              bandsGate.error instanceof Error
                ? bandsGate.error.message
                : 'Could not load school bands for this year.'
            }
          />
        ) : null}
        {yearDigits && bandsGate.notConfigured ? (
          <ClassificationWorkflowRequiredAlert yearLabel={yearLabel || year} />
        ) : null}

        {showProfileBody && !activeSchool ? (
          <div className="flex min-h-[48vh] flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300/90 bg-gradient-to-b from-white to-slate-50/80 px-6 py-16 text-center shadow-md ring-1 ring-slate-100">
            <div className="flex size-16 items-center justify-center rounded-2xl bg-slate-100 shadow-inner ring-1 ring-slate-200/80">
              <Building2 className="size-8 text-slate-400" strokeWidth={1.25} aria-hidden />
            </div>
            <p className="mt-5 text-lg font-bold text-slate-800">Select filters to load the profile</p>
            <p className="mt-2 max-w-lg text-sm text-slate-600">
              {isParentPortal ? (
                <>
                  Pick governorate, <strong>reporting year</strong>, and <strong>school</strong> to open the
                  family-focused profile: <strong>tuition (USD)</strong> and a peer comparison.
                </>
              ) : isSchoolAdmin ? (
                <>
                  Choose a <strong>reporting year</strong>. The server returns your assigned school; if there is only
                  one, it is selected automatically. You can still open the list to confirm or search by name or code.
                </>
              ) : (
                <>
                  Choose a <strong>governorate</strong>, then a <strong>reporting year</strong> (school search uses that
                  year on the server). Finally pick a <strong>school</strong> by name or centre code. Data appears only
                  when all required fields are set.
                </>
              )}
            </p>
            {!isSchoolAdmin && governorateId && !year ? (
              <p className="mt-3 text-sm font-medium text-amber-900">
                Select a reporting year to enable the school list.
              </p>
            ) : null}
            {selectedSchoolDetail?.id !== selectedSchoolId && selectedSchoolId ? (
              <p className="mt-3 text-sm font-medium text-amber-900">
                School details could not be resolved; pick the school again from the list.
              </p>
            ) : null}
          </div>
        ) : showProfileBody && activeSchool && isParentPortal ? (
          <SchoolProfileParentLoadedView
            school={activeSchool}
            year={year}
            reportingYearLabel={yearLabel || year}
            scopeLabel={scopeLabel}
            publicationRuleLabel={publicationRuleLabel}
            profileViewMode={profileViewMode}
            dashboard={dashboardFromProfile}
            dashboardLoading={profileQuery.isLoading}
            dashboardError={profileErrorMessage}
            comparison={profileQuery.data}
            comparisonLoading={profileQuery.isLoading}
            comparisonError={profileErrorMessage}
          />
        ) : showProfileBody && activeSchool ? (
          <SchoolProfileLoadedView
            school={activeSchool}
            year={year}
            reportingYearLabel={yearLabel || year}
            scopeLabel={scopeLabel}
            publicationRuleLabel={publicationRuleLabel}
            profileViewMode={profileViewMode}
            dashboard={dashboardFromProfile}
            dashboardLoading={profileQuery.isLoading}
            dashboardError={profileErrorMessage}
            comparison={profileQuery.data}
            comparisonLoading={profileQuery.isLoading}
            comparisonError={profileErrorMessage}
          />
        ) : null}
      </div>
    </div>
  );
}
